/**
 * ItemScreensTab — Item Master Screen Definition Builder
 * Attribute Studio → Item Screens
 * Design mode : per-field control-type selector, visible/readonly/required toggles, DB-bind panel
 * Preview mode: rendered form controls for the active tab (industry-specific)
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Trash2, Save, Search, ChevronDown, ChevronRight,
  GripVertical, X, Eye, Settings, Download, Database, RefreshCw, Check, AlertTriangle
} from 'lucide-react';
import { erpToast } from '../../components/erp/ERPToast';
import { API_BASE as API_URL, getToken } from '../../lib/api';
import GetInfoPopup from '../../components/GetInfoPopup';

const hdrs = () => ({ Authorization: `Bearer ${getToken() || ''}`, 'Content-Type': 'application/json' });

// ─── Static SearchInfo fallback map (mirrors ItemMasterDynamic ATTR_SEARCHINFO_MAP) ─
const AE_ATTR_SI_MAP = {
  division:    { screenId: '5.5.8',  controlId: 1 },
  department:  { screenId: '5.5.11', controlId: 1 },
  category:    { screenId: '5.5.2',  controlId: 1 },
  subcategory: { screenId: '5.5.12', controlId: 1 },
  brand:       { screenId: '5.5.3',  controlId: 1 },
  style:       { screenId: '5.5.10', controlId: 1 },
  color:       { screenId: '5.5.4',  controlId: 1 },
  size:        { screenId: '5.5.9',  controlId: 1 },
  design:      { screenId: '5.5.5',  controlId: 1 },
  inputtaxname:  { screenId: '4.8.1', controlId: 16 },
  outputtaxname: { screenId: '4.8.1', controlId: 17 },
  baseunitname:  { screenId: '4.8.1', controlId: 6 },
  uom:           { screenId: '4.8.1', controlId: 6 },
};

// ─── Control-type groups (matching reference HTML) ──────────────────────────
const CONTROL_TYPE_GROUPS = [
  { group: 'Basic', options: [
    { value: 'text', label: 'Text' }, { value: 'number', label: 'Number' },
    { value: 'textarea', label: 'Textarea' }, { value: 'checkbox', label: 'Checkbox' },
    { value: 'radio', label: 'Radio Group' }, { value: 'select', label: 'Dropdown (Select)' },
    { value: 'toggle', label: 'Toggle / Switch' }, { value: 'date', label: 'Date' },
    { value: 'time', label: 'Time' }, { value: 'datetime', label: 'Date & Time' },
    { value: 'email', label: 'Email' }, { value: 'phone', label: 'Phone' },
    { value: 'password', label: 'Password' }, { value: 'url', label: 'URL' },
  ]},
  { group: 'Advanced', options: [
    { value: 'lookup', label: 'Lookup (GetInfo)' }, { value: 'autocomplete', label: 'Autocomplete' },
    { value: 'multiselect', label: 'Multi-Select' }, { value: 'combo', label: 'Editable Combo' },
    { value: 'cascadingselect', label: 'Cascading Dropdown' }, { value: 'masked', label: 'Masked Input' },
    { value: 'currency', label: 'Currency' }, { value: 'percentage', label: 'Percentage' },
    { value: 'slider', label: 'Slider / Range' }, { value: 'rating', label: 'Rating' },
    { value: 'tags', label: 'Tags' }, { value: 'richtext', label: 'Rich Text Editor' },
    { value: 'signature', label: 'Signature Pad' }, { value: 'fileupload', label: 'File Upload' },
    { value: 'imagepicker', label: 'Image Picker' }, { value: 'barcode', label: 'Barcode / QR Scan' },
    { value: 'colorpicker', label: 'Colour Picker' }, { value: 'treepicker', label: 'Tree Picker' },
    { value: 'subgrid', label: 'Sub-Grid (Table)' }, { value: 'otp', label: 'OTP Input' },
  ]},
  { group: 'DevOps / System', options: [
    { value: 'formula', label: 'Formula (Calculated)' }, { value: 'apibound', label: 'API-Bound Field' },
    { value: 'dbquery', label: 'DB Query Field' }, { value: 'storedproc', label: 'Stored Procedure Trigger' },
    { value: 'webhookbutton', label: 'Webhook Action Button' }, { value: 'script', label: 'Script / Expression' },
    { value: 'featureflag', label: 'Feature Flag Toggle' }, { value: 'auditlog', label: 'Audit Log Viewer' },
    { value: 'envtag', label: 'Environment Tag' }, { value: 'integrationstatus', label: 'Integration Status' },
    { value: 'permissiongate', label: 'Role / Permission Gate' },
  ]},
];

// Map type → category for badge display
const TYPE_CAT = {};
CONTROL_TYPE_GROUPS.forEach(g => g.options.forEach(o => { TYPE_CAT[o.value] = g.group.split(' ')[0].toLowerCase(); }));

// DB data types
const DB_DATA_TYPES = ['nvarchar', 'varchar', 'int', 'bigint', 'decimal', 'float', 'bit', 'datetime2', 'date', 'uniqueidentifier'];

// Compute a hash of pricing/tax attrs for snapshot freshness detection
const hashAttrs = (attrs) => {
  if (!attrs || attrs.length === 0) return '';
  return [...attrs]
    .filter(a => ['grp_pricing', 'grp_tax'].includes(a.GroupId))
    .sort((a, b) => a.AttributeId.localeCompare(b.AttributeId))
    .map(a => `${a.AttributeId}:${a.GroupId}`)
    .join('|');
};

// Country-aware tax label map (mirrors AttributeStudioScreen)
const TAX_LABEL_MAP_IST = {
  GST:         { inputTaxName: 'GST Input (Purchase)', outputTaxName: 'GST Output (Sales)', cessValue: 'Cess %', hsnTaxRate: 'HSN Tax Rate', baseUnitName: 'Base Unit' },
  VAT:         { inputTaxName: 'VAT Input (Purchase)', outputTaxName: 'VAT Output (Sales)', cessValue: 'VAT Exempt %', hsnTaxRate: 'HS Code Rate', baseUnitName: 'Base Unit' },
  'Sales Tax': { inputTaxName: 'Sales Tax Input', outputTaxName: 'Sales Tax Output', cessValue: 'Surtax %', hsnTaxRate: 'HTS Rate', baseUnitName: 'Base Unit' },
};
const applyTaxLabelIST = (attr, taxSystem) => {
  if (!taxSystem || attr.GroupId !== 'grp_tax') return attr;
  const map = TAX_LABEL_MAP_IST[taxSystem];
  if (!map || !map[attr.AttributeId]) return attr;
  return { ...attr, Label: map[attr.AttributeId] };
};

// AE ControlType → preview field type mapping
const AE_CONTROL_TYPE_MAP = {
  LookUpEdit: 'lookup', LookupEdit: 'lookup',
  ComboBoxEdit: 'combo',
  CalcEdit: 'currency', SpinEdit: 'number',
  DateEdit: 'date',
  CheckEdit: 'checkbox',
  TextEdit: 'text', MemoEdit: 'textarea',
};

// Field factory helper
const F = (id, label, type = 'text', opts = {}) => ({
  id, label, type, visible: true, readonly: false, required: false, db: null, ...opts,
});

// ─── Default screen definition — 7 tabs, full field coverage ────────────────
const DEFAULT_SCREEN_DEF = {
  tabs: [
    {
      id: 'sku', label: 'SKU / PLU Info',
      sections: [
        {
          id: 'product_id', label: 'Product Identification', accent: '#1e40af', columns: 3,
          fields: [
            F('transNo',        'Trans No',          'text',    { readonly: true }),
            F('skuPlu',         'SKU / PLU',         'text',    { required: true }),
            F('eanCode',        'EAN / OEM Barcode', 'barcode'),
            F('manufacturerName','Manufacturer',     'lookup'),
            F('articleCode',    'Article Code',      'text'),
            F('articleName',    'Article Name',      'text'),
            F('itemName',       'Item Name',         'text',    { required: true }),
            F('hsnCode',        'HSN Code',          'text'),
          ],
        },
        {
          id: 'classification', label: 'Classification', accent: '#3730a3', columns: 3, type: 'ae-classification',
          includeGroups: ['grp_classification', 'grp_style'],
          fields: [],
        },
        {
          id: 'product_attributes', label: 'Product Attributes', accent: '#2E6BE0', columns: 3, type: 'ae-classification',
          includeGroups: ['grp_physical', 'grp_compliance'],
          fields: [],
        },
        {
          id: 'procurement', label: 'Procurement Details', accent: '#0369a1', columns: 3,
          fields: [
            F('agentName',       'Agent',          'lookup'),
            F('supplierName',    'Supplier',       'lookup', { required: true }),
            F('paymentTerms',    'Payment Terms',  'lookup'),
            F('purchOfficerName','Purch Officer',  'lookup'),
            F('creditPeriod',    'Credit Period',  'number'),
            F('interest',        'Interest %',     'percentage'),
          ],
        },
        {
          id: 'pricing', label: 'Pricing', accent: '#b45309', columns: 4,
          type: 'ae-classification', includeGroups: ['grp_pricing'],
          fields: [], // Derived from AE Library grp_pricing — add fields there, they appear here automatically
        },
        {
          id: 'tax', label: 'Tax & Compliance', accent: '#991b1b', columns: 3,
          type: 'ae-classification', includeGroups: ['grp_tax'],
          fields: [], // Derived from AE Library grp_tax — country-configurable tax fields
        },
        {
          id: 'inventory', label: 'Inventory Thresholds', accent: '', columns: 5,
          fields: [
            F('minQty',    'Min Qty',    'number'),
            F('rol',       'ROL',        'number'),
            F('maxQty',    'Max Qty',    'number'),
            F('leadTime',  'Lead Time',  'number'),
            F('expPeriod', 'Exp Period', 'number'),
          ],
        },
      ],
    },
    {
      id: 'units', label: 'Multiple Units',
      sections: [{
        id: 'units_s', label: 'Unit Configuration', accent: '#3730a3', columns: 4,
        fields: [
          F('unitName',          'Unit Name',         'lookup', { required: true }),
          F('unitCode',          'Unit Code',         'text'),
          F('conversionFactor',  'Conversion Factor', 'number', { required: true }),
          F('unitMrp',           'MRP',               'currency'),
          F('unitRate',          'Rate',              'currency'),
          F('priceLevel',        'Price Level',       'select'),
          F('unitStatus',        'Status',            'toggle'),
          F('isDefaultUnit',     'Is Default',        'toggle'),
        ],
      }],
    },
    {
      id: 'rates', label: 'Multiple Rates',
      sections: [{
        id: 'rates_s', label: 'Price List / Rates', accent: '#0d7377', columns: 4,
        fields: [
          F('rateLevel',    'Rate Level',  'text',       { required: true }),
          F('rateName',     'Rate Name',   'text'),
          F('rateValue',    'Rate Value',  'currency',   { required: true }),
          F('rateTax',      'Tax %',       'percentage'),
          F('rateDisc',     'Disc %',      'percentage'),
          F('rateStatus',   'Status',      'toggle'),
          F('rateFromDate', 'From Date',   'date'),
        ],
      }],
    },
    {
      id: 'suppliers', label: 'Other Suppliers',
      sections: [{
        id: 'suppliers_s', label: 'Other Supplier Details', accent: '#b45309', columns: 3,
        fields: [
          F('supplierCode',       'Supplier Code',   'text'),
          F('otherSupplierName',  'Supplier Name',   'lookup', { required: true }),
          F('contactPerson',      'Contact Person',  'text'),
          F('contactPhone',       'Contact Phone',   'phone'),
          F('contactEmail',       'Contact Email',   'email'),
          F('supplierAddress',    'Address',         'textarea'),
          F('supplierCity',       'City',            'text'),
          F('accountNo',          'Account No',      'text'),
        ],
      }],
    },
    {
      id: 'control', label: 'Control Option',
      sections: [{
        id: 'control_s', label: 'Item Control Flags', accent: '#1e40af', columns: 4,
        fields: [
          F('isActive',        'Active',            'toggle'),
          F('isSerialized',    'Serialized',        'toggle'),
          F('isBatchCtrl',     'Batch Controlled',  'toggle'),
          F('isExpiryTracked', 'Expiry Tracked',    'toggle'),
          F('isWeighable',     'Weighable',         'toggle'),
          F('canFraction',     'Allow Fraction',    'toggle'),
          F('trackInventory',  'Track Inventory',   'toggle'),
          F('posVisible',      'POS Visible',       'toggle'),
          F('webVisible',      'Web Visible',       'toggle'),
          F('importAllowed',   'Import Allowed',    'toggle'),
          F('labelRequired',   'Label Required',    'toggle'),
          F('allowReturns',    'Allow Returns',     'toggle'),
          F('allowDiscount',   'Allow Discount',    'toggle'),
        ],
      }],
    },
    {
      id: 'branding', label: 'Branding',
      sections: [{
        id: 'branding_s', label: 'Branding & Revenue', accent: '#be185d', columns: 3,
        fields: [
          F('brandName',      'Brand',          'lookup'),
          F('divisionBrand',  'Division',       'lookup'),
          F('deptBrand',      'Department',     'lookup'),
          F('styleName',      'Style',          'lookup'),
          F('designName',     'Design',         'lookup'),
          F('marketingNote',  'Marketing Note', 'textarea'),
        ],
      }],
    },
    {
      id: 'import', label: 'Import',
      sections: [{
        id: 'import_s', label: 'Bulk Import', accent: '#0369a1', columns: 2,
        fields: [
          F('importFile',     'Import File', 'fileupload', { required: true }),
          F('importTemplate', 'Template',    'select'),
        ],
      }],
    },
  ],
};

// ─── Normalize field: string ID → rich object (backward compat) ──────────────
const FIELD_LABELS = {
  transNo: 'Trans No', skuPlu: 'SKU / PLU', eanCode: 'EAN/OEM Barcode',
  manufacturerName: 'Manufacturer', articleCode: 'Article Code', articleName: 'Article Name',
  itemName: 'Item Name', hsnCode: 'HSN Code', agentName: 'Agent', supplierName: 'Supplier',
  paymentTerms: 'Payment Terms', purchOfficerName: 'Purch Officer', creditPeriod: 'Credit Period',
  interest: 'Interest %',
  // grp_pricing (all — AE-driven)
  priceQty: 'Price Qty', freeQty: 'Free Qty', mrp: 'MRP', costRate: 'Cost / Purchase Rate',
  wholesaleRate: 'Wholesale Rate', sellingWspPrice: 'Selling WSP Price', discountType: 'Discount Type',
  discountValue: 'Discount Value', wsp: 'Sales Price', rate: 'Selling Rate', discPer: 'Disc %',
  purchDisc: 'Purch Disc %', ownMrp: 'Own MRP', listMrp: 'List MRP',
  // grp_tax (all — AE-driven, country-configurable)
  inputTaxName: 'Input Tax (Purchase)', outputTaxName: 'Output Tax (Sales)',
  cessValue: 'Cess %', hsnTaxRate: 'HSN Tax Rate', baseUnitName: 'Base Unit',
  minQty: 'Min Qty', rol: 'ROL', maxQty: 'Max Qty', leadTime: 'Lead Time', expPeriod: 'Exp Period',
  division: 'Division', department: 'Department', category: 'Category', subcategory: 'Sub Category',
  brand: 'Brand', style: 'Style', color: 'Colour', design: 'Design', size: 'Size', uom: 'UOM',
};

const normalizeField = (f) => {
  if (typeof f === 'string') return F(f, FIELD_LABELS[f] || f);
  return { visible: true, readonly: false, required: false, db: null, type: 'text', ...f };
};

// ─── Merge saved def with DEFAULT tabs — deep merge ─────────────────────────
// Alias map: old section IDs that have been renamed → new canonical ID
const SECTION_ID_ALIASES = { procurement_ae: 'product_attributes' };

function mergeWithDefaults(saved) {
  const base = JSON.parse(JSON.stringify(DEFAULT_SCREEN_DEF));
  if (!saved || !saved.tabs) return base;

  // Normalize saved tabs: migrate renamed section IDs
  const normalizedSaved = {
    ...saved,
    tabs: (saved.tabs || []).map(tab => ({
      ...tab,
      sections: (tab.sections || []).map(sec => ({
        ...sec,
        id: SECTION_ID_ALIASES[sec.id] || sec.id,
      })),
    })),
  };

  // Deep merge: for each saved tab, enrich sparse sections from defaults
  const mergedTabs = normalizedSaved.tabs.map(savedTab => {
    const defaultTab = base.tabs.find(t => t.id === savedTab.id);
    if (!defaultTab) return savedTab;

    // Count total fields in the saved tab
    const totalSavedFields = (savedTab.sections || []).reduce((sum, s) => sum + (s.fields || []).length, 0);

    // If tab is very sparse (≤ 2 total fields), replace sections with rich defaults
    if (totalSavedFields <= 2) {
      return { ...savedTab, sections: defaultTab.sections };
    }

    // Otherwise, add any default sections that are missing from the saved tab
    const savedSecIds = new Set((savedTab.sections || []).map(s => s.id));
    const missingSecs = (defaultTab.sections || []).filter(s => !savedSecIds.has(s.id));
    if (!missingSecs.length) return savedTab;
    return { ...savedTab, sections: [...(savedTab.sections || []), ...missingSecs] };
  });

  // Add tabs that exist in defaults but not in saved
  const savedTabIds = new Set(mergedTabs.map(t => t.id));
  return { ...normalizedSaved, tabs: [...mergedTabs, ...base.tabs.filter(t => !savedTabIds.has(t.id))] };
}

// ─── Available fields for the field picker ────────────────────────────────────
const FIELD_CATALOG = {
  'Product Identification': ['transNo', 'skuPlu', 'eanCode', 'manufacturerName', 'articleCode', 'articleName', 'itemName', 'hsnCode'],
  'Procurement': ['agentName', 'supplierName', 'paymentTerms', 'purchOfficerName', 'creditPeriod', 'interest'],
  'Pricing': ['priceQty', 'freeQty', 'mrp', 'rate', 'wsp', 'discPer', 'purchDisc', 'ownMrp', 'listMrp'],
  'Tax': ['inputTaxName', 'outputTaxName', 'cessValue', 'hsnTaxRate', 'baseUnitName'],
  'Inventory': ['minQty', 'rol', 'maxQty', 'leadTime', 'expPeriod'],
};

// ─── Accent color presets ─────────────────────────────────────────────────────
const ACCENT_COLORS = [
  { label: 'Blue', value: '#1e40af' }, { label: 'Indigo', value: '#3730a3' },
  { label: 'Teal', value: '#0d7377' }, { label: 'Amber', value: '#b45309' },
  { label: 'Rose', value: '#be185d' }, { label: 'Sky', value: '#0369a1' },
  { label: 'None', value: '' },
];

// ─── Toggle switch component ──────────────────────────────────────────────────
const ToggleSwitch = ({ value, onChange, disabled }) => (
  <label style={{ cursor: disabled ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center', position: 'relative', width: 28, height: 14, flexShrink: 0 }}>
    <input type="checkbox" checked={!!value} onChange={e => !disabled && onChange && onChange(e.target.checked)}
      style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }} />
    <span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: 14, background: value ? '#2563eb' : '#d1d5db', transition: 'background 0.15s' }} />
    <span style={{ position: 'absolute', left: value ? 16 : 2, top: 2, width: 10, height: 10, borderRadius: '50%', background: '#fff', transition: 'left 0.15s', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }} />
  </label>
);

// ─── DB bind panel (per field, collapsible) ──────────────────────────────────
const DbBindPanel = ({ db, fieldType, onChange }) => {
  const d = db || {};
  const set = (k, v) => onChange({ ...d, [k]: v });
  const inp = { fontSize: 10, padding: '2px 5px', border: '1px solid #e2e8f0', borderRadius: 3, background: '#fff', width: '100%' };
  const sel = { ...inp, cursor: 'pointer' };
  const isLookupType = ['lookup', 'autocomplete', 'treepicker', 'cascadingselect', 'combo', 'multiselect'].includes(fieldType);

  return (
    <div style={{ padding: '6px 8px', background: '#f8faff', borderTop: '1px solid #e0e7ff' }}>
      {/* Row 1: basic binding */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr) repeat(3,1fr) auto', gap: 4, alignItems: 'end' }}>
        <div>
          <div style={{ fontSize: 9, color: '#64748b', marginBottom: 2 }}>Source type</div>
          <select style={sel} value={d.source || 'table'} onChange={e => set('source', e.target.value)}>
            {['static', 'table', 'view', 'storedproc', 'api'].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 9, color: '#64748b', marginBottom: 2 }}>Object / Table</div>
          <input style={inp} placeholder="e.g. ItemMaster" value={d.object || ''} onChange={e => set('object', e.target.value)} />
        </div>
        <div>
          <div style={{ fontSize: 9, color: '#64748b', marginBottom: 2 }}>Column</div>
          <input style={inp} placeholder="e.g. ItemCode" value={d.column || ''} onChange={e => set('column', e.target.value)} />
        </div>
        <div>
          <div style={{ fontSize: 9, color: '#64748b', marginBottom: 2 }}>Data type</div>
          <select style={sel} value={d.dataType || 'nvarchar'} onChange={e => set('dataType', e.target.value)}>
            {DB_DATA_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 9, color: '#64748b', marginBottom: 2 }}>Max length</div>
          <input style={inp} type="number" placeholder="100" value={d.maxLength || ''} onChange={e => set('maxLength', e.target.value)} />
        </div>
        <div>
          <div style={{ fontSize: 9, color: '#64748b', marginBottom: 2 }}>Default value</div>
          <input style={inp} placeholder="(none)" value={d.defaultValue || ''} onChange={e => set('defaultValue', e.target.value)} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <div style={{ fontSize: 9, color: '#64748b' }}>Nullable</div>
          <ToggleSwitch value={d.nullable !== false} onChange={v => set('nullable', v)} />
        </div>
        <button onClick={() => onChange(null)} title="Remove DB bind"
          style={{ padding: '2px 6px', fontSize: 10, border: '1px solid #fca5a5', color: '#dc2626', borderRadius: 3, cursor: 'pointer', background: '#fff7f7', alignSelf: 'end' }}>
          <X size={10} />
        </button>
      </div>

      {/* Row 2: SearchInfo lookup binding — only for lookup-type fields */}
      {isLookupType && (
        <div style={{ marginTop: 8, padding: '6px 8px', background: '#eff6ff', borderRadius: 4, border: '1px solid #bfdbfe' }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: '#1d4ed8', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
            <Database size={10} /> SearchInfo Binding (SP_GetColumns / SP_TableName architecture)
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 4, marginBottom: 4 }}>
            <div>
              <div style={{ fontSize: 9, color: '#64748b', marginBottom: 2 }}>ScreenId <span style={{ color: '#dc2626' }}>*</span></div>
              <input style={inp} placeholder="e.g. 5.5.8" value={d.screenId || ''} onChange={e => set('screenId', e.target.value)} />
            </div>
            <div>
              <div style={{ fontSize: 9, color: '#64748b', marginBottom: 2 }}>ControlId <span style={{ color: '#dc2626' }}>*</span></div>
              <input style={inp} type="number" placeholder="e.g. 1" value={d.controlId || ''} onChange={e => set('controlId', parseInt(e.target.value) || '')} />
            </div>
            <div>
              <div style={{ fontSize: 9, color: '#64748b', marginBottom: 2 }}>Display column (DisplayMember)</div>
              <input style={inp} placeholder="e.g. DivName" value={d.lookupDisplayField || ''} onChange={e => set('lookupDisplayField', e.target.value)} />
            </div>
            <div>
              <div style={{ fontSize: 9, color: '#64748b', marginBottom: 2 }}>Value column (ValueMember / PK)</div>
              <input style={inp} placeholder="e.g. DivUId" value={d.lookupValueField || ''} onChange={e => set('lookupValueField', e.target.value)} />
            </div>
            <div>
              <div style={{ fontSize: 9, color: '#64748b', marginBottom: 2 }}>WHERE condition</div>
              <input style={inp} placeholder="e.g. DivName&lt;&gt;'NA'" value={d.whereCondition || ''} onChange={e => set('whereCondition', e.target.value)} />
            </div>
            <div>
              <div style={{ fontSize: 9, color: '#64748b', marginBottom: 2 }}>ORDER BY</div>
              <input style={inp} placeholder="e.g. DivUId" value={d.orderBy || ''} onChange={e => set('orderBy', e.target.value)} />
            </div>
            <div>
              <div style={{ fontSize: 9, color: '#64748b', marginBottom: 2 }}>Status column</div>
              <input style={inp} placeholder="Status" value={d.statusColumn || ''} onChange={e => set('statusColumn', e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'end', paddingBottom: 2 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <div style={{ fontSize: 8, color: '#64748b' }}>Auto load</div>
                <ToggleSwitch value={d.autoLoad !== false} onChange={v => set('autoLoad', v)} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <div style={{ fontSize: 8, color: '#64748b' }}>Predictive</div>
                <ToggleSwitch value={!!d.predictiveSearch} onChange={v => set('predictiveSearch', v)} />
              </div>
            </div>
          </div>
          <div>
            <div style={{ fontSize: 9, color: '#64748b', marginBottom: 2 }}>Query override (raw SQL — uses @P1=CompId, @P2=Status)</div>
            <textarea style={{ ...inp, height: 44, resize: 'vertical', fontFamily: 'monospace', fontSize: 9 }}
              placeholder="SELECT TOP 50 DivName 'Div Name', DivUId 'Div Id' FROM DivisionMaster WHERE CompId=@P1 AND Status=@P2"
              value={d.queryOverride || ''} onChange={e => set('queryOverride', e.target.value)} />
          </div>
          {d.screenId && d.controlId && (
            <div style={{ marginTop: 4, fontSize: 9, color: '#16a34a', display: 'flex', alignItems: 'center', gap: 3 }}>
              <Check size={10} /> Will use: GET /api/inventory/getinfo/screen/{d.screenId}/control/{d.controlId}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Preview control renderer ────────────────────────────────────────────────
const PreviewControl = ({ field, onLookupOpen }) => {
  const f = normalizeField(field);
  const base = { width: '100%', border: '1px solid #e2e8f0', borderRadius: 3, padding: '3px 6px', fontSize: 11, background: f.readonly ? '#f8faff' : '#fff', outline: 'none' };
  const hasSearchInfo = !!(f.db?.screenId && f.db?.controlId);
  switch (f.type) {
    case 'lookup': case 'autocomplete': case 'treepicker':
      return <div style={{ display: 'flex', gap: 3 }}>
        <input style={{ ...base, flex: 1 }} placeholder={`Select ${f.label}...`} readOnly={f.readonly} />
        <button onClick={() => onLookupOpen && onLookupOpen(f)}
          style={{ padding: '2px 7px', border: `1px solid ${hasSearchInfo ? '#6366f1' : '#e2e8f0'}`, borderRadius: 3, fontSize: 11, cursor: 'pointer', background: hasSearchInfo ? '#eef2ff' : '#f8faff', color: hasSearchInfo ? '#4f46e5' : '#64748b', minWidth: 28, textAlign: 'center' }}
          title={hasSearchInfo ? `SearchInfo: Screen ${f.db.screenId} / Ctrl ${f.db.controlId}` : 'Lookup (no SearchInfo configured)'}>
          …
        </button>
      </div>;
    case 'select': case 'combo': case 'multiselect': case 'cascadingselect':
      return <select style={{ ...base, cursor: 'pointer' }}><option value="">-- Select --</option></select>;
    case 'toggle':
      return <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}><ToggleSwitch value={false} onChange={() => {}} /><span style={{ fontSize: 10, color: '#64748b' }}>Off</span></div>;
    case 'checkbox':
      return <input type="checkbox" style={{ cursor: 'pointer', width: 14, height: 14 }} />;
    case 'date': case 'datetime': case 'time':
      return <input type={f.type === 'datetime' ? 'datetime-local' : f.type} style={base} readOnly={f.readonly} />;
    case 'number': case 'currency': case 'percentage': case 'slider':
      return <input type="number" style={base} placeholder="0" readOnly={f.readonly} />;
    case 'textarea': case 'richtext':
      return <textarea style={{ ...base, height: 48, resize: 'vertical', fontFamily: 'inherit' }} placeholder={f.label} readOnly={f.readonly} />;
    case 'fileupload':
      return <div style={{ display: 'flex', gap: 3 }}><input style={{ ...base, flex: 1, cursor: 'pointer' }} value="No file selected" readOnly /><button style={{ padding: '2px 8px', border: '1px solid #e2e8f0', borderRadius: 3, fontSize: 10, cursor: 'pointer', background: '#f8faff' }}>Browse</button></div>;
    case 'imagepicker':
      return <div style={{ display: 'flex', gap: 3 }}><input style={{ ...base, flex: 1 }} placeholder="No image" readOnly /><button style={{ padding: '2px 8px', border: '1px solid #e2e8f0', borderRadius: 3, fontSize: 10, cursor: 'pointer', background: '#f8faff' }}>Pick</button></div>;
    case 'barcode':
      return <div style={{ display: 'flex', gap: 3 }}><input style={{ ...base, flex: 1 }} placeholder="Scan or enter..." readOnly={f.readonly} /><button style={{ padding: '2px 6px', border: '1px solid #e2e8f0', borderRadius: 3, fontSize: 10, cursor: 'pointer', background: '#f8faff' }}>⊡</button></div>;
    case 'subgrid':
      return <div style={{ border: '1px solid #e2e8f0', borderRadius: 3, padding: 4, fontSize: 10, color: '#94a3b8', textAlign: 'center' }}>[ Sub-Grid Table ]</div>;
    case 'formula':
      return <input style={{ ...base, background: '#f1f5f9', fontStyle: 'italic', color: '#64748b' }} placeholder="= calculated…" readOnly />;
    case 'apibound': case 'dbquery':
      return <div style={{ display: 'flex', gap: 3 }}><input style={{ ...base, flex: 1, color: '#64748b' }} placeholder="API / Query field" readOnly /><button style={{ padding: '2px 7px', border: '1px solid #e2e8f0', borderRadius: 3, fontSize: 10, background: '#f8faff', cursor: 'pointer' }}>↺</button></div>;
    case 'rating':
      return <div style={{ display: 'flex', gap: 2 }}>{[1,2,3,4,5].map(i => <span key={i} style={{ fontSize: 16, color: '#fbbf24', cursor: 'pointer' }}>☆</span>)}</div>;
    case 'colorpicker':
      return <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}><input type="color" style={{ width: 28, height: 22, padding: 0, border: '1px solid #e2e8f0', borderRadius: 3, cursor: 'pointer' }} /><input style={{ ...base, flex: 1 }} placeholder="#000000" readOnly={f.readonly} /></div>;
    default:
      return <input type={f.type === 'email' ? 'email' : f.type === 'phone' ? 'tel' : f.type === 'password' ? 'password' : f.type === 'url' ? 'url' : 'text'} style={base} placeholder={f.label} readOnly={f.readonly} />;
  }
};

// ─── Safe formula evaluator (shared) ─────────────────────────────────────────
function evaluateFormula(formula, values) {
  if (!formula?.trim()) return null;
  try {
    let expr = formula.replace(/\{(\w+)\}/g, (_, id) => {
      const v = parseFloat(values?.[id]);
      return isNaN(v) ? '0' : String(v);
    });
    expr = expr.replace(/\bround\s*\(/gi, '__round(')
               .replace(/\babs\s*\(/gi, '__abs(')
               .replace(/\bmin\s*\(/gi, '__min(')
               .replace(/\bmax\s*\(/gi, '__max(')
               .replace(/\bif\s*\(/gi, '__if(');
    // eslint-disable-next-line no-new-func
    const result = new Function('__round','__abs','__min','__max','__if',`"use strict"; return (${expr})`)(
      (x, n) => Math.round(x * Math.pow(10, n == null ? 2 : n)) / Math.pow(10, n == null ? 2 : n),
      Math.abs, Math.min, Math.max,
      (cond, then, els) => cond ? then : els,
    );
    return typeof result === 'number' && isFinite(result) ? result : null;
  } catch { return null; }
}

// ─── Precision round (tally-safe) ───────────────────────────────────────────
const precRound = (v, dp = 2) => Math.round(v * Math.pow(10, dp)) / Math.pow(10, dp);

// ─── Formula Builder Modal ────────────────────────────────────────────────────
const FormulaBuilderModal = ({ attr, snapshot, onSave, onClose }) => {
  const [formula, setFormula] = useState(attr?.Formula || '');
  const [testValues, setTestValues] = useState({});
  const [dp, setDp] = useState(attr?.decimalPlaces || 2);
  const [previewResult, setPreviewResult] = useState(null);
  const [error, setError] = useState('');

  const allFields = (snapshot?.attrs || []).filter(a => ['grp_pricing','grp_tax'].includes(a.GroupId));

  useEffect(() => {
    if (!formula.trim()) { setPreviewResult(null); setError(''); return; }
    const result = evaluateFormula(formula, testValues);
    if (result === null) { setError('Invalid expression — check syntax'); setPreviewResult(null); }
    else { setError(''); setPreviewResult(precRound(result, dp).toFixed(dp)); }
  }, [formula, testValues, dp]);

  const insertText = (text) => setFormula(prev => prev + text);
  const usedFields = allFields.filter(f => formula.includes(`{${f.AttributeId}}`));

  const OPERATORS = ['+','-','*','/','^','(',')','%', ' '];
  const FUNCTIONS = [
    { label: 'round(x, n)', insert: 'round(, 2)' },
    { label: 'abs(x)', insert: 'abs()' },
    { label: 'min(a,b)', insert: 'min(, )' },
    { label: 'max(a,b)', insert: 'max(, )' },
    { label: 'if(cond,t,f)', insert: 'if(, , )' },
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: '#fff', borderRadius: 12, width: 860, maxWidth: '96vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,.25)' }}>
        {/* Header */}
        <div style={{ padding: '10px 16px', borderBottom: '1px solid #e2e8f0', background: '#f8faff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>Formula Builder</span>
            <span style={{ marginLeft: 8, fontSize: 11, color: '#64748b' }}>→ {attr?.Label || attr?.AttributeId}</span>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 18, color: '#94a3b8', lineHeight: 1 }}>×</button>
        </div>
        {/* Body */}
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
          {/* Left: available fields */}
          <div style={{ width: 210, borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '6px 10px 4px', fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', flexShrink: 0 }}>Click to insert field</div>
            <div style={{ overflow: 'auto', flex: 1 }}>
              {allFields.map(f => (
                <button key={f.AttributeId} onClick={() => insertText(`{${f.AttributeId}}`)}
                  title={`Insert {${f.AttributeId}}`}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '5px 10px', fontSize: 10, border: 'none', borderBottom: '1px solid #f8faff', cursor: 'pointer', background: formula.includes(`{${f.AttributeId}}`) ? '#eff6ff' : '#fff' }}>
                  <div style={{ fontWeight: 600, color: '#1e293b' }}>{f.Label || f.AttributeId}</div>
                  <div style={{ fontSize: 8, color: '#94a3b8', fontFamily: 'monospace' }}>{`{${f.AttributeId}}`}</div>
                </button>
              ))}
            </div>
          </div>
          {/* Center: editor */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 12, overflow: 'hidden' }}>
            {/* Operator buttons */}
            <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', marginBottom: 8 }}>
              {OPERATORS.map((op, i) => (
                <button key={i} onClick={() => insertText(op === ' ' ? ' ' : op)}
                  style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, width: op === ' ' ? 24 : 32, height: 24, border: '1px solid #e2e8f0', borderRadius: 4, cursor: 'pointer', background: '#f8faff', color: '#1e40af' }}>
                  {op === ' ' ? '·' : op}
                </button>
              ))}
              {FUNCTIONS.map(fn => (
                <button key={fn.label} onClick={() => insertText(fn.insert)}
                  style={{ fontFamily: 'monospace', fontSize: 9, padding: '2px 8px', height: 24, border: '1px solid #e2e8f0', borderRadius: 4, cursor: 'pointer', background: '#fef9c3', color: '#92400e' }}>
                  {fn.label}
                </button>
              ))}
            </div>
            {/* Formula textarea */}
            <div style={{ fontSize: 9, color: '#64748b', marginBottom: 4 }}>Formula (use <code style={{ background:'#f1f5f9', padding:'0 3px', borderRadius:2 }}>{'{fieldId}'}</code> for field values):</div>
            <textarea
              value={formula}
              onChange={e => setFormula(e.target.value)}
              style={{ flex: 1, minHeight: 70, fontFamily: 'monospace', fontSize: 12, padding: 8, border: `1px solid ${error ? '#fca5a5' : '#bfdbfe'}`, borderRadius: 5, outline: 'none', resize: 'none', lineHeight: 1.7, color: '#1e293b' }}
              placeholder="{mrp} * (1 - {discPer} / 100)"
              spellCheck={false}
              data-testid="formula-textarea"
            />
            {error && <div style={{ fontSize: 10, color: '#dc2626', marginTop: 4 }}>⚠ {error}</div>}
            {!error && formula && <div style={{ fontSize: 10, color: '#16a34a', marginTop: 4 }}>✓ Expression valid</div>}
            {/* Decimal places */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, padding: '6px 8px', background: '#f8faff', borderRadius: 5, border: '1px solid #e2e8f0' }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: '#475569' }}>Decimal places:</span>
              {[2,3,4].map(n => (
                <button key={n} onClick={() => setDp(n)}
                  style={{ fontSize: 11, width: 30, height: 24, border: '1px solid #e2e8f0', borderRadius: 4, cursor: 'pointer', background: dp === n ? '#2563eb' : '#fff', color: dp === n ? '#fff' : '#64748b', fontWeight: dp === n ? 700 : 400 }}>
                  {n}
                </button>
              ))}
              <span style={{ fontSize: 9, color: '#94a3b8' }}>Default 2 · use 4 for tally-safe reverse calc</span>
            </div>
          </div>
          {/* Right: test values + preview */}
          <div style={{ width: 210, borderLeft: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '6px 10px 4px', fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', flexShrink: 0 }}>Test values</div>
            <div style={{ overflow: 'auto', flex: 1, padding: '4px 10px' }}>
              {usedFields.length === 0 && <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 8 }}>Insert a field from the left panel to see test inputs here.</div>}
              {usedFields.map(f => (
                <div key={f.AttributeId} style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 9, color: '#475569', marginBottom: 2, fontWeight: 600 }}>{f.Label || f.AttributeId}</div>
                  <input type="number" value={testValues[f.AttributeId] || ''} placeholder="0"
                    onChange={e => setTestValues(prev => ({ ...prev, [f.AttributeId]: e.target.value }))}
                    style={{ width: '100%', fontSize: 11, padding: '3px 6px', border: '1px solid #e2e8f0', borderRadius: 3, outline: 'none' }} />
                </div>
              ))}
              {previewResult !== null && (
                <div style={{ marginTop: 12, padding: '10px 8px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, textAlign: 'center' }}>
                  <div style={{ fontSize: 9, color: '#15803d', marginBottom: 4 }}>Preview Result</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#166534', fontFamily: 'monospace' }}>{previewResult}</div>
                  <div style={{ fontSize: 8, color: '#86efac', marginTop: 2 }}>{dp} decimal places · tally-safe</div>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Footer */}
        <div style={{ padding: '8px 16px', borderTop: '1px solid #e2e8f0', background: '#f8faff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ fontSize: 9, color: '#94a3b8' }}>Fill test values → verify preview → Save. Formula only activates on CalcEdit fields.</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => { setFormula(''); setPreviewResult(null); setError(''); }}
              style={{ fontSize: 10, padding: '4px 12px', border: '1px solid #e2e8f0', borderRadius: 4, cursor: 'pointer', background: '#fff', color: '#64748b' }}>Clear</button>
            <button onClick={onClose}
              style={{ fontSize: 10, padding: '4px 12px', border: '1px solid #e2e8f0', borderRadius: 4, cursor: 'pointer', background: '#fff', color: '#64748b' }}>Cancel</button>
            <button onClick={() => { if (!error) onSave(formula, dp); }} disabled={!!error && !!formula}
              data-testid="formula-save-btn"
              style={{ fontSize: 10, padding: '4px 14px', border: 'none', borderRadius: 4, cursor: (!!error && !!formula) ? 'default' : 'pointer', background: (!!error && !!formula) ? '#e2e8f0' : '#2563eb', color: (!!error && !!formula) ? '#94a3b8' : '#fff', fontWeight: 600 }}>
              Save Formula
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Field picker dropdown ────────────────────────────────────────────────────
const FieldPicker = ({ onAdd, existingIds }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [customLabel, setCustomLabel] = useState('');
  const [customType, setCustomType] = useState('text');
  const [showCustom, setShowCustom] = useState(false);

  const filtered = Object.entries(FIELD_CATALOG).map(([cat, ids]) => ({
    cat,
    fields: ids.filter(id => !existingIds.includes(id) && (!search || (FIELD_LABELS[id] || id).toLowerCase().includes(search.toLowerCase()))),
  })).filter(g => g.fields.length > 0);

  return (
    <div>
      {!open ? (
        <button onClick={() => setOpen(true)} data-testid="add-field-btn"
          style={{ fontSize: 9, padding: '2px 8px', border: '1px dashed #d1d5db', borderRadius: 3, cursor: 'pointer', background: '#fff', display: 'inline-flex', alignItems: 'center', gap: 3, color: '#64748b' }}>
          <Plus size={10} /> Add Field
        </button>
      ) : (
        <div style={{ border: '1px solid #bfdbfe', borderRadius: 6, padding: 8, background: '#fff', boxShadow: '0 4px 14px rgba(0,0,0,.09)', marginTop: 4 }}>
          <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
            <input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder="Search fields..."
              style={{ fontSize: 10, height: 24, flex: 1, border: '1px solid #e2e8f0', borderRadius: 3, padding: '0 6px', outline: 'none' }} />
            <button onClick={() => { setOpen(false); setSearch(''); setShowCustom(false); }}
              style={{ fontSize: 11, padding: '0 6px', border: '1px solid #e2e8f0', borderRadius: 3, cursor: 'pointer', background: '#f8faff' }}>×</button>
          </div>
          <div style={{ maxHeight: 180, overflowY: 'auto', marginBottom: 6 }}>
            {filtered.map(({ cat, fields }) => (
              <div key={cat}>
                <div style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', padding: '3px 4px', background: '#f8faff', borderRadius: 3, marginBottom: 2 }}>{cat}</div>
                {fields.map(id => (
                  <button key={id} onClick={() => { onAdd(F(id, FIELD_LABELS[id] || id)); setOpen(false); setSearch(''); }}
                    style={{ display: 'block', width: '100%', textAlign: 'left', fontSize: 10, padding: '3px 8px', cursor: 'pointer', background: 'transparent', border: 'none', borderRadius: 3, color: '#1e293b' }}
                    onMouseEnter={e => { if (e.currentTarget) e.currentTarget.style.background = '#f1f5f9'; }}
                    onMouseLeave={e => { if (e.currentTarget) e.currentTarget.style.background = 'transparent'; }}>
                    {FIELD_LABELS[id] || id}
                  </button>
                ))}
              </div>
            ))}
            {filtered.length === 0 && <div style={{ fontSize: 10, color: '#94a3b8', padding: 8, textAlign: 'center' }}>No more fields to add</div>}
          </div>
          <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 6 }}>
            {!showCustom ? (
              <button onClick={() => setShowCustom(true)} style={{ fontSize: 9, color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>+ Custom field</button>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <input autoFocus value={customLabel} onChange={e => setCustomLabel(e.target.value)} placeholder="Field label"
                  onKeyDown={e => e.key === 'Enter' && document.getElementById('__fld_add_btn')?.click()}
                  style={{ fontSize: 10, height: 24, border: '1px solid #e2e8f0', borderRadius: 3, padding: '0 6px', outline: 'none' }} />
                <select value={customType} onChange={e => setCustomType(e.target.value)}
                  style={{ fontSize: 10, height: 24, border: '1px solid #e2e8f0', borderRadius: 3, padding: '0 5px' }}>
                  {CONTROL_TYPE_GROUPS.flatMap(g => g.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>))}
                </select>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button id="__fld_add_btn" onClick={() => {
                    if (!customLabel.trim()) return;
                    const id = customLabel.trim().replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '').toLowerCase();
                    onAdd(F(id || `field_${Date.now()}`, customLabel.trim(), customType));
                    setOpen(false); setCustomLabel(''); setShowCustom(false); setSearch('');
                  }} style={{ flex: 1, fontSize: 9, padding: '3px 8px', border: '1px solid #2563eb', color: '#2563eb', borderRadius: 3, cursor: 'pointer', background: '#eff6ff', fontWeight: 600 }}>
                    ✓ Add Field
                  </button>
                  <button onClick={() => { setShowCustom(false); setCustomLabel(''); }}
                    style={{ fontSize: 9, padding: '3px 8px', border: '1px solid #e2e8f0', borderRadius: 3, cursor: 'pointer', background: '#fff' }}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main component ────────────────────────────────────────────────────────────
const ItemScreensTab = ({ industries, taxSystem }) => {
  const [mode, setMode] = useState('design'); // 'design' | 'preview'
  const [selectedIndustryId, setSelectedIndustryId] = useState(
    () => localStorage.getItem('item_screens_industry') || ''
  );
  const [screenDef, setScreenDef] = useState(null);
  const [activeTabIdx, setActiveTabIdx] = useState(0);
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [industrySearch, setIndustrySearch] = useState('');
  const [newTabName, setNewTabName] = useState('');
  const [newSectionName, setNewSectionName] = useState('');
  const [definedIndustries, setDefinedIndustries] = useState([]);
  const [dbBindOpen, setDbBindOpen] = useState(null); // key: `${tabIdx}-${secIdx}-${fIdx}`
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [previewGetInfo, setPreviewGetInfo] = useState(null); // { title, screenId, controlId, tableName }
  // AE attribute snapshot: baked into the config on save to avoid repeated API calls on form load
  const [aeSnapshotData, setAeSnapshotData] = useState(null);
  const [snapshotStale, setSnapshotStale] = useState(false);
  // Pending formula edits in design mode (attrId → formula string, saved on blur)
  const [pendingFormulas, setPendingFormulas] = useState({});
  // Formula Builder Modal state
  const [formulaBuilderAttr, setFormulaBuilderAttr] = useState(null); // attr object to edit

  // ── AE Design helpers ──────────────────────────────────────────────────────────
  // Get attrs for a given AE section (filtered by includeGroups or safe default)
  const getAeSectionAttrs = (section) => {
    if (!aeSnapshotData?.attrs) return [];
    const inc = section.includeGroups || null;
    const PRICING_EXCL = new Set(['grp_pricing', 'grp_tax']);
    return aeSnapshotData.attrs.filter(a => {
      const gid = a.GroupId || '(none)';
      if (inc?.length) return inc.includes(gid);
      return !PRICING_EXCL.has(gid); // catch-all: exclude pricing/tax
    });
  };

  // Toggle visible / IsRequired / readonly on a snapshot attr; persists immediately
  const toggleAeAttrDesign = async (attrId, flag) => {
    if (!aeSnapshotData?.attrs) return;
    const newAttrs = aeSnapshotData.attrs.map(a => {
      if (a.AttributeId !== attrId) return a;
      if (flag === 'visible') return { ...a, visible: a.visible === false ? true : false };
      if (flag === 'required') return { ...a, IsRequired: !a.IsRequired };
      if (flag === 'readonly') return { ...a, readonly: !a.readonly };
      return a;
    });
    const newSnap = { ...aeSnapshotData, attrs: newAttrs };
    setAeSnapshotData(newSnap);
    await persistDef({ ...(screenDef || {}), aeAttributeSnapshot: newSnap }, { quiet: true });
  };

  // Save ParentAttributeId for a classification attr — updates DB mapping + snapshot
  const saveAttrParent = async (attrId, parentAttrId) => {
    if (!selectedIndustryId || !aeSnapshotData?.attrs) return;
    const newAttrs = aeSnapshotData.attrs.map(a =>
      a.AttributeId === attrId ? { ...a, ParentAttributeId: parentAttrId || null } : a
    );
    const newSnap = { ...aeSnapshotData, attrs: newAttrs };
    setAeSnapshotData(newSnap);
    await persistDef({ ...(screenDef || {}), aeAttributeSnapshot: newSnap }, { quiet: true });
    // Also persist to DB mapping
    fetch(`${API_URL}/api/attribute-engine/industries/${selectedIndustryId}/attr-parent`, {
      method: 'PATCH', headers: hdrs(),
      body: JSON.stringify({ attrId, parentAttrId: parentAttrId || null }),
    }).catch(() => {});
  };

  // Save formula for a CalcEdit attr (called on blur of formula input)
  const saveAttrFormula = async (attrId, formula, dp) => {
    if (!aeSnapshotData?.attrs) return;
    const newAttrs = aeSnapshotData.attrs.map(a =>
      a.AttributeId === attrId ? { ...a, Formula: formula || null, decimalPlaces: dp ?? a.decimalPlaces ?? 2 } : a
    );
    const newSnap = { ...aeSnapshotData, attrs: newAttrs };
    setAeSnapshotData(newSnap);
    await persistDef({ ...(screenDef || {}), aeAttributeSnapshot: newSnap }, { quiet: true });
  };

  // Save hierarchy type per section
  const saveHierarchyType = async (tabIdx, secIdx, hType) => {
    mutDef(d => { d.tabs[tabIdx].sections[secIdx].hierarchyType = hType; });
    // auto-save with a slight delay via existing save
    setTimeout(() => saveDef(), 300);
  };

  // Save decimal places for a pricing/tax attr
  const saveAttrDecimalPlaces = async (attrId, dp) => {
    if (!aeSnapshotData?.attrs) return;
    const newAttrs = aeSnapshotData.attrs.map(a =>
      a.AttributeId === attrId ? { ...a, decimalPlaces: dp } : a
    );
    const newSnap = { ...aeSnapshotData, attrs: newAttrs };
    setAeSnapshotData(newSnap);
    await persistDef({ ...(screenDef || {}), aeAttributeSnapshot: newSnap }, { quiet: true });
  };

  // Load defined industries list
  useEffect(() => {
    fetch(`${API_URL}/api/attribute-engine/screen-definitions`, { headers: hdrs() })
      .then(r => r.json())
      .then(j => { if (j.success) setDefinedIndustries(j.data.map(d => d.IndustryId)); })
      .catch(() => {});
  }, []);

  const loadDef = useCallback(async (industryId) => {
    if (!industryId) return;
    setLoading(true);
    try {
      // Fetch screen def + AE effective-config in parallel
      const [defR, cfgR] = await Promise.all([
        fetch(`${API_URL}/api/attribute-engine/screen-definitions/${industryId}`, { headers: hdrs() }),
        fetch(`${API_URL}/api/attribute-engine/industries/${industryId}/effective-config`, { headers: hdrs() }),
      ]);
      const j = await defR.json();
      const cfgJ = await cfgR.json();

      if (j.success && j.data?.config) {
        setScreenDef(mergeWithDefaults(j.data.config));
      } else {
        setScreenDef(JSON.parse(JSON.stringify(DEFAULT_SCREEN_DEF)));
      }

      // Build AE snapshot from effective-config (GroupLabel + ParentAttributeId included per field)
      if (cfgJ.success && cfgJ.data?.groups?.length) {
        const seen = new Set();
        const attrs = cfgJ.data.groups.flatMap(g =>
          g.fields.map(f => ({ ...f, GroupId: f.GroupId || g.groupId, GroupLabel: g.label }))
        ).filter(a => {
          if (seen.has(a.AttributeId)) return false; // deduplicate by AttributeId
          seen.add(a.AttributeId);
          return true;
        }).map(a => applyTaxLabelIST(a, taxSystem));
        const groups = cfgJ.data.groups.map(g => ({ GroupId: g.groupId, Label: g.label }));
        const currentHash = hashAttrs(attrs);
        // Merge Formula / visible / readonly / required overrides from previously saved snapshot
        const savedAttrs = j.data?.config?.aeAttributeSnapshot?.attrs || [];
        const savedByAttrId = {};
        savedAttrs.forEach(a => { savedByAttrId[a.AttributeId] = a; });
        const mergedAttrs = attrs.map(a => {
          const saved = savedByAttrId[a.AttributeId];
          if (!saved) return a;
          return {
            ...a,
            visible: saved.visible,
            IsRequired: saved.IsRequired ?? a.IsRequired,
            readonly: saved.readonly,
            Formula: saved.Formula || a.Formula,
            ParentAttributeId: a.ParentAttributeId || saved.ParentAttributeId, // DB value takes precedence
          };
        });
        setAeSnapshotData({ attrs: mergedAttrs, groups, hash: currentHash });

        // Check snapshot freshness: compare current attr hash with baked hash
        const bakedHash = j.data?.config?.aeAttributeSnapshot?.hash || '';
        setSnapshotStale(!!bakedHash && bakedHash !== currentHash);

        // Auto-bake snapshot for legacy screen definitions that don't have one yet
        if (j.success && j.data?.config && !j.data.config.aeAttributeSnapshot) {
          const snap = { attrs: mergedAttrs, groups, hash: currentHash };
          const legacyConfig = mergeWithDefaults(j.data.config);
          fetch(`${API_URL}/api/attribute-engine/screen-definitions/${industryId}`, {
            method: 'PUT', headers: hdrs(),
            body: JSON.stringify({ config: { ...legacyConfig, aeAttributeSnapshot: snap }, screenName: 'Product Master' }),
          }).catch(() => {}); // fire-and-forget — best effort migration
        }
      } else {
        setAeSnapshotData(null);
        setSnapshotStale(false);
      }

      setActiveTabIdx(0);
      setActiveSectionId(null);
    } catch { setScreenDef(JSON.parse(JSON.stringify(DEFAULT_SCREEN_DEF))); setAeSnapshotData(null); }
    setLoading(false);
  }, []);

  const selectIndustry = (id) => {
    localStorage.setItem('item_screens_industry', id);
    setSelectedIndustryId(id);
    loadDef(id);
  };

  // Auto-load on mount if a persisted industry is set
  useEffect(() => {
    if (selectedIndustryId) loadDef(selectedIndustryId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper to persist a given config JSON to DB
  const persistDef = useCallback(async (configToSave, { quiet = false } = {}) => {
    if (!selectedIndustryId) return false;
    setSaveStatus('Saving...');
    try {
      // configToSave.aeAttributeSnapshot takes precedence over state (caller may pass updated snapshot)
      const finalConfig = { aeAttributeSnapshot: aeSnapshotData, ...configToSave };
      const r = await fetch(`${API_URL}/api/attribute-engine/screen-definitions/${selectedIndustryId}`, {
        method: 'PUT', headers: hdrs(),
        body: JSON.stringify({ config: finalConfig, screenName: 'Product Master' }),
      });
      const j = await r.json();
      if (j.success) {
        setSaveStatus('Saved to DB');
        if (!quiet) erpToast.success(`Screen definition saved for ${selectedIndustryId}`);
        if (!definedIndustries.includes(selectedIndustryId)) setDefinedIndustries(p => [...p, selectedIndustryId]);
        setTimeout(() => setSaveStatus(''), 2500);
        return true;
      } else { setSaveStatus('Error'); if (!quiet) erpToast.error(j.error || 'Save failed'); }
    } catch { setSaveStatus('Error'); if (!quiet) erpToast.error('Save failed'); }
    return false;
  }, [selectedIndustryId, aeSnapshotData, definedIndustries]);

  const saveDef = async () => {
    if (!selectedIndustryId || !screenDef) return;
    setSaving(true);
    await persistDef(screenDef);
    setSaving(false);
  };

  // Toggle AE attribute visibility in snapshot; saves to DB
  const toggleAttrVisibility = async (groupId, attrIdxInGroup) => {
    if (!aeSnapshotData?.attrs) return;
    const newAttrs = aeSnapshotData.attrs.map((a, globalIdx) => {
      const gAttrs = aeSnapshotData.attrs.filter(x => (x.GroupId || '(none)') === groupId);
      if ((a.GroupId || '(none)') === groupId && gAttrs.indexOf(a) === attrIdxInGroup) {
        return { ...a, visible: a.visible === false ? true : false };
      }
      return a;
    });
    const newSnap = { ...aeSnapshotData, attrs: newAttrs };
    setAeSnapshotData(newSnap);
    await persistDef({ ...(screenDef || {}), aeAttributeSnapshot: newSnap }, { quiet: true });
  };

  // Move AE attribute up/down within its group; saves updated order to snapshot in DB
  // AND re-indexes DisplayOrder in AE_IndustryAttributeMap permanently
  const moveAttrInGroup = async (groupId, attrIdxInGroup, direction) => {
    if (!aeSnapshotData?.attrs) return;
    const newAttrs = [...aeSnapshotData.attrs];
    // Collect global indices of attrs belonging to this group
    const groupIdxs = newAttrs.reduce((acc, a, i) => {
      if ((a.GroupId || '(none)') === groupId) acc.push(i);
      return acc;
    }, []);
    const newGroupPos = direction === 'up' ? attrIdxInGroup - 1 : attrIdxInGroup + 1;
    if (newGroupPos < 0 || newGroupPos >= groupIdxs.length) return;
    const gi = groupIdxs[attrIdxInGroup];
    const gj = groupIdxs[newGroupPos];
    [newAttrs[gi], newAttrs[gj]] = [newAttrs[gj], newAttrs[gi]];
    const newSnap = { ...aeSnapshotData, attrs: newAttrs };
    setAeSnapshotData(newSnap);
    // Save snapshot to screen definition
    await persistDef({ ...(screenDef || {}), aeAttributeSnapshot: newSnap }, { quiet: true });
    // Re-index DisplayOrder permanently in AE_IndustryAttributeMap
    if (selectedIndustryId) {
      const orderedAttrIds = newAttrs.map(a => a.AttributeId).filter(Boolean);
      fetch(`${API_URL}/api/attribute-engine/industries/${selectedIndustryId}/attributes/reorder`, {
        method: 'PUT', headers: hdrs(),
        body: JSON.stringify({ attrIds: orderedAttrIds }),
      }).catch(() => {}); // fire-and-forget; snapshot already saved
    }
  };

  // Re-fetch effective-config and re-bake the AE snapshot (use after Attribute Library/Industry Setup changes)
  const refreshSnapshot = useCallback(async () => {
    if (!selectedIndustryId) return;
    setSaveStatus('Refreshing snapshot…');
    try {
      const cfgR = await fetch(`${API_URL}/api/attribute-engine/industries/${selectedIndustryId}/effective-config`, { headers: hdrs() });
      const cfgJ = await cfgR.json();
      if (cfgJ.success && cfgJ.data?.groups?.length) {
        const seen = new Set();
        const attrs = cfgJ.data.groups.flatMap(g =>
          g.fields.map(f => ({ ...f, GroupId: f.GroupId || g.groupId, GroupLabel: g.label }))
        ).filter(a => {
          if (seen.has(a.AttributeId)) return false;
          seen.add(a.AttributeId);
          return true;
        }).map(a => applyTaxLabelIST(a, taxSystem));
        const groups = cfgJ.data.groups.map(g => ({ GroupId: g.groupId, Label: g.label }));
        // Preserve formula/visibility overrides from current snapshot
        const curByAttrId = {};
        (aeSnapshotData?.attrs || []).forEach(a => { curByAttrId[a.AttributeId] = a; });
        const mergedAttrs = attrs.map(a => {
          const cur = curByAttrId[a.AttributeId];
          if (!cur) return a;
          return { ...a, visible: cur.visible, IsRequired: cur.IsRequired ?? a.IsRequired, readonly: cur.readonly, Formula: cur.Formula || a.Formula };
        });
        const newSnapshot = { attrs: mergedAttrs, groups, hash: hashAttrs(attrs) };
        setAeSnapshotData(newSnapshot);
        setSnapshotStale(false);
        // Immediately persist updated snapshot + current screen def
        const r = await fetch(`${API_URL}/api/attribute-engine/screen-definitions/${selectedIndustryId}`, {
          method: 'PUT', headers: hdrs(),
          body: JSON.stringify({ config: { ...(screenDef || {}), aeAttributeSnapshot: newSnapshot }, screenName: 'Product Master' }),
        });
        const j = await r.json();
        setSaveStatus(j.success ? `Saved to DB (${attrs.length} attrs)` : 'Error saving snapshot');
        setTimeout(() => setSaveStatus(''), 3000);
      } else {
        setSaveStatus('No attrs found for this industry');
        setTimeout(() => setSaveStatus(''), 2500);
      }
    } catch { setSaveStatus('Error'); setTimeout(() => setSaveStatus(''), 2000); }
  }, [selectedIndustryId, screenDef]);

  const exportJson = () => {
    if (!screenDef) return;
    const blob = new Blob([JSON.stringify(screenDef, null, 2)], { type: 'application/json' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `screen-def-${selectedIndustryId || 'export'}.json`; a.click();
  };

  // Mutation helpers
  const mutDef = (fn) => setScreenDef(prev => { const d = JSON.parse(JSON.stringify(prev)); fn(d); return d; });

  const addTab = () => {
    if (!newTabName.trim()) return;
    mutDef(d => d.tabs.push({ id: `tab_${Date.now()}`, label: newTabName.trim(), sections: [] }));
    setNewTabName('');
    setActiveTabIdx((screenDef?.tabs?.length) || 0);
  };

  const deleteTab = (idx) => { mutDef(d => d.tabs.splice(idx, 1)); setActiveTabIdx(0); };
  const renameTab = (idx, label) => mutDef(d => { d.tabs[idx].label = label; });

  const addSection = (tabIdx) => {
    if (!newSectionName.trim()) return;
    const id = `sec_${Date.now()}`;
    mutDef(d => d.tabs[tabIdx].sections.push({ id, label: newSectionName.trim(), accent: '#1e40af', columns: 3, fields: [] }));
    setNewSectionName(''); setActiveSectionId(id);
  };

  const deleteSection = (tabIdx, secIdx) => { mutDef(d => d.tabs[tabIdx].sections.splice(secIdx, 1)); setActiveSectionId(null); };

  const updateSection = (tabIdx, secIdx, key, val) => mutDef(d => { d.tabs[tabIdx].sections[secIdx][key] = val; });

  const addFieldToSection = (tabIdx, secIdx, fieldObj) => {
    mutDef(d => {
      const fields = d.tabs[tabIdx].sections[secIdx].fields;
      const exists = fields.some(f => (typeof f === 'string' ? f : f.id) === fieldObj.id);
      if (!exists) fields.push(fieldObj);
    });
  };

  const removeFieldFromSection = (tabIdx, secIdx, fieldIdx) => {
    mutDef(d => d.tabs[tabIdx].sections[secIdx].fields.splice(fieldIdx, 1));
  };

  const updateField = (tabIdx, secIdx, fieldIdx, patch) => {
    mutDef(d => {
      const f = normalizeField(d.tabs[tabIdx].sections[secIdx].fields[fieldIdx]);
      d.tabs[tabIdx].sections[secIdx].fields[fieldIdx] = { ...f, ...patch };
    });
  };

  // Filtered
  const filteredIndustries = industrySearch
    ? industries.filter(i => i.IndustryName.toLowerCase().includes(industrySearch.toLowerCase()))
    : industries;

  const tabs = screenDef?.tabs || [];
  const activeTab = tabs[activeTabIdx];

  // ─── Shared toolbar ─────────────────────────────────────────────────────────
  const renderToolbar = () => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 12px', borderBottom: '1px solid #e2e8f0', background: '#f8faff', flexShrink: 0, flexWrap: 'wrap', rowGap: 4, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'nowrap', minWidth: 0, flex: '0 1 auto' }}>
        {/* Industry dropdown in toolbar for easy switching */}
        <select
          value={selectedIndustryId}
          onChange={e => selectIndustry(e.target.value)}
          data-testid="toolbar-industry-select"
          style={{ fontSize: 11, fontWeight: 700, color: '#1e293b', border: '1px solid #bfdbfe', borderRadius: 4, padding: '2px 20px 2px 8px', background: '#eff6ff', cursor: 'pointer', outline: 'none', height: 26, maxWidth: 200 }}>
          <option value="">— Select Industry —</option>
          {industries.map(ind => (
            <option key={ind.IndustryId} value={ind.IndustryId}>{ind.IndustryName || ind.IndustryId}</option>
          ))}
        </select>
        {/* Mode switch — inline, never wraps */}
        <div style={{ display: 'flex', border: '1px solid #e2e8f0', borderRadius: 4, overflow: 'hidden', flexShrink: 0 }}>
          {[{ m: 'design', icon: <Settings size={11} />, label: 'Design' }, { m: 'preview', icon: <Eye size={11} />, label: 'Preview' }].map(({ m, icon, label }) => (
            <button key={m} onClick={() => setMode(m)} data-testid={`mode-${m}`}
              style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '3px 8px', fontSize: 10, border: 'none', cursor: 'pointer', background: mode === m ? '#2563eb' : '#fff', color: mode === m ? '#fff' : '#64748b', fontWeight: mode === m ? 700 : 400, whiteSpace: 'nowrap' }}>
              {icon}{label}
            </button>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap', flex: '0 1 auto' }}>
        {saveStatus && (
          <span data-testid="save-status-indicator"
            style={{ fontSize: 10, color: saveStatus.includes('Saved') ? '#16a34a' : saveStatus === 'Error' ? '#dc2626' : '#64748b', display: 'flex', alignItems: 'center', gap: 3 }}>
            {saveStatus.includes('Saved') && <Check size={9} />}{saveStatus}
          </span>
        )}
        {aeSnapshotData?.attrs?.length > 0 && (
          <span title={`AE snapshot: ${aeSnapshotData.attrs.length} attributes baked-in`}
            style={{ fontSize: 8, background: '#dcfce7', color: '#15803d', borderRadius: 8, padding: '1px 6px', fontWeight: 700 }}>
            AE snapshot ({aeSnapshotData.attrs.length})
          </span>
        )}
        <button onClick={exportJson} title="Export JSON"
          style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, padding: '3px 8px', border: '1px solid #e2e8f0', borderRadius: 3, cursor: 'pointer', background: '#fff', color: '#64748b' }}>
          <Download size={11} /> Export JSON
        </button>
        <button onClick={refreshSnapshot} title="Re-bake AE attributes from current Industry Config (use after Attribute Library or Industry Setup changes)"
          data-testid="refresh-snapshot-btn"
          style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, padding: '3px 8px', border: '1px solid #bfdbfe', borderRadius: 3, cursor: 'pointer', background: '#eff6ff', color: '#2563eb' }}>
          <RefreshCw size={11} /> Refresh AE Snapshot
        </button>
        <button onClick={() => loadDef(selectedIndustryId)} title="Reset to saved"
          style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, padding: '3px 8px', border: '1px solid #e2e8f0', borderRadius: 3, cursor: 'pointer', background: '#fff', color: '#64748b' }}>
          <RefreshCw size={11} /> Reset
        </button>
        <button onClick={saveDef} disabled={saving}
          style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, padding: '3px 10px', border: 'none', borderRadius: 3, cursor: saving ? 'default' : 'pointer', background: '#2563eb', color: '#fff', fontWeight: 600 }} data-testid="save-config-btn">
          <Save size={11} /> {saving ? 'Saving…' : 'Save configuration'}
        </button>
      </div>
    </div>
  );

  // ─── Tab bar (shared) ────────────────────────────────────────────────────────
  const toggleTabEnabled = async (i) => {
    // Optimistic UI update
    const newDef = JSON.parse(JSON.stringify(screenDef));
    newDef.tabs[i].enabled = newDef.tabs[i].enabled === false ? true : false;
    setScreenDef(newDef);
    // Auto-save immediately so Item Master picks up the change
    await persistDef(newDef, { quiet: true });
  };

  const renderTabBar = () => (
    <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #e2e8f0', padding: '4px 12px', gap: 3, flexWrap: 'wrap', background: '#fff', flexShrink: 0 }}>
      {tabs.map((tab, i) => {
        const isEnabled = tab.enabled !== false;
        return (
          <div key={tab.id} style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {activeTabIdx === i && mode === 'design' ? (
              <input value={tab.label} onChange={e => renameTab(i, e.target.value)}
                style={{ fontSize: 10, height: 24, width: Math.max(60, tab.label.length * 7), border: '1px solid #bfdbfe', borderRadius: 3, padding: '0 5px', background: '#eff6ff', fontWeight: 700, outline: 'none', opacity: isEnabled ? 1 : 0.5 }} />
            ) : (
              <button onClick={() => { setActiveTabIdx(i); setActiveSectionId(null); }}
                style={{ fontSize: 10, padding: '3px 9px', border: `1px solid ${activeTabIdx === i ? '#bfdbfe' : '#e2e8f0'}`, borderRadius: 3, cursor: 'pointer', background: activeTabIdx === i ? '#eff6ff' : '#fff', color: activeTabIdx === i ? '#1d4ed8' : '#475569', fontWeight: activeTabIdx === i ? 700 : 400, opacity: isEnabled ? 1 : 0.45, textDecoration: isEnabled ? 'none' : 'line-through' }}>
                {tab.label}
              </button>
            )}
            {/* Enable/Disable toggle — licensor controls tab visibility in Item Master */}
            {mode === 'design' && (
              <button
                onClick={e => { e.stopPropagation(); toggleTabEnabled(i); }}
                title={isEnabled ? 'Tab is ON — click to hide in Item Master' : 'Tab is OFF — click to show in Item Master'}
                data-testid={`tab-enable-${tab.id}`}
                style={{ fontSize: 8, padding: '1px 5px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: isEnabled ? '#dcfce7' : '#fee2e2',
                  color: isEnabled ? '#16a34a' : '#dc2626',
                  fontWeight: 700, lineHeight: 1.4 }}>
                {isEnabled ? 'ON' : 'OFF'}
              </button>
            )}
            {mode === 'design' && tabs.length > 1 && (
              <button onClick={() => deleteTab(i)} style={{ fontSize: 9, padding: '0 3px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#dc2626', opacity: 0.7 }} title="Delete tab">×</button>
            )}
          </div>
        );
      })}
      {mode === 'design' && (
        <div style={{ display: 'flex', gap: 3, alignItems: 'center', marginLeft: 6 }}>
          <input value={newTabName} onChange={e => setNewTabName(e.target.value)} placeholder="New tab…"
            style={{ fontSize: 10, height: 24, width: 85, border: '1px solid #e2e8f0', borderRadius: 3, padding: '0 5px', outline: 'none' }}
            onKeyDown={e => e.key === 'Enter' && addTab()} />
          <button onClick={addTab} style={{ fontSize: 9, padding: '2px 7px', border: '1px solid #e2e8f0', borderRadius: 3, cursor: 'pointer', background: '#fff', color: '#2563eb' }}>+ Tab</button>
        </div>
      )}
    </div>
  );

  // ─── Design mode — per-field table ─────────────────────────────────────────
  const renderDesignMode = () => {
    if (!activeTab) return null;
    return (
      <div style={{ flex: 1, overflow: 'auto', padding: 12 }}>
        {/* Tab-level settings: default row count for grid tabs */}
        {['units', 'rates'].includes(activeTab.id) && (
          <div style={{ marginBottom: 10, padding: '7px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 5, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: '#166534' }}>Default Row Count</span>
            <input type="number" min={1} max={20} value={activeTab.defaultRowCount || 3}
              onChange={e => mutDef(d => { d.tabs[activeTabIdx].defaultRowCount = Math.max(1, Math.min(20, Number(e.target.value))); })}
              style={{ width: 54, fontSize: 10, padding: '2px 6px', border: '1px solid #86efac', borderRadius: 3, outline: 'none' }} />
            <span style={{ fontSize: 9, color: '#15803d' }}>rows shown when creating a new item (1–20)</span>
          </div>
        )}
        {activeTab.sections.map((section, sIdx) => {
          const expanded = activeSectionId === section.id;
          const fieldCount = section.fields.length;
          return (
            <div key={section.id} style={{ border: `1px solid ${expanded ? '#bfdbfe' : '#e2e8f0'}`, borderRadius: 6, marginBottom: 8, overflow: 'hidden' }}>
              {/* Section header */}
              <div onClick={() => setActiveSectionId(expanded ? null : section.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px', background: '#f8faff', cursor: 'pointer', borderLeft: `4px solid ${section.accent || '#e2e8f0'}` }}>
                {expanded ? <ChevronDown size={12} color="#64748b" /> : <ChevronRight size={12} color="#64748b" />}
                <input value={section.label} onChange={e => updateSection(activeTabIdx, sIdx, 'label', e.target.value)}
                  onClick={e => e.stopPropagation()}
                  style={{ fontSize: 11, fontWeight: 700, height: 22, width: 200, background: 'transparent', border: '1px solid transparent', outline: 'none', cursor: 'text', padding: '0 3px' }}
                  onFocus={e => { e.target.style.border = '1px solid #e2e8f0'; e.target.style.background = '#fff'; }}
                  onBlur={e => { e.target.style.border = '1px solid transparent'; e.target.style.background = 'transparent'; }} />
                <span style={{ fontSize: 9, color: '#94a3b8' }}>{fieldCount} field{fieldCount !== 1 ? 's' : ''}</span>
                {section.type === 'ae-classification' && <span style={{ fontSize: 8, background: '#dbeafe', color: '#1d4ed8', borderRadius: 8, padding: '1px 5px' }}>AE-driven</span>}
                <div style={{ display: 'flex', gap: 4, marginLeft: 'auto', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
                  {ACCENT_COLORS.map(c => (
                    <button key={c.value} title={c.label} onClick={() => updateSection(activeTabIdx, sIdx, 'accent', c.value)}
                      style={{ width: 13, height: 13, borderRadius: '50%', border: section.accent === c.value ? '2px solid #1e293b' : '1px solid #e2e8f0', background: c.value || '#f1f5f9', cursor: 'pointer', flexShrink: 0 }} />
                  ))}
                  <select value={section.columns || 3} onChange={e => updateSection(activeTabIdx, sIdx, 'columns', parseInt(e.target.value))}
                    style={{ fontSize: 9, height: 20, border: '1px solid #e2e8f0', borderRadius: 3, padding: '0 3px', marginLeft: 4, cursor: 'pointer' }} onClick={e => e.stopPropagation()}>
                    {[1, 2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n} col</option>)}
                  </select>
                  <button onClick={() => deleteSection(activeTabIdx, sIdx)}
                    style={{ marginLeft: 4, padding: '1px 5px', fontSize: 9, border: '1px solid #fca5a5', color: '#dc2626', borderRadius: 3, cursor: 'pointer', background: '#fff7f7' }}>
                    <Trash2 size={10} />
                  </button>
                </div>
              </div>

              {/* Fields table */}
              {expanded && (
                <div>
                  {section.type === 'ae-classification' ? (
                    /* AE-driven section — show snapshot attrs with hierarchy + formula config */
                    (() => {
                      const aeAttrs = getAeSectionAttrs(section);
                      const isClassif = section.includeGroups?.some(g => ['grp_classification','grp_style','grp_physical','grp_compliance'].includes(g)) || !section.includeGroups;
                      const isPricingOrTax = section.includeGroups?.some(g => ['grp_pricing','grp_tax'].includes(g));
                      const currentHType = section.hierarchyType || 'vertical';
                      return (
                        <div style={{ borderTop: '1px solid #bfdbfe' }}>
                          {/* Info bar */}
                          <div style={{ padding: '4px 12px', fontSize: 9, color: '#1d4ed8', background: '#eff6ff', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span>AE-driven — {aeAttrs.length} attributes from Industry Config Matrix</span>
                            {!aeAttrs.length && <span style={{ color: '#dc2626', fontWeight: 600 }}>No snapshot yet — click <strong>Refresh AE Snapshot</strong> above</span>}
                          </div>
                          {/* Hierarchy type selector for classification sections */}
                          {isClassif && aeAttrs.length > 0 && (
                            <div style={{ padding: '4px 12px', background: '#f0f9ff', borderBottom: '1px solid #bae6fd', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: 9, color: '#0369a1', fontWeight: 700 }}>Hierarchy Type:</span>
                              {[
                                { id: 'vertical', label: '↓ Vertical', desc: 'Parent→Child chain · child auto-clears on parent change' },
                                { id: 'horizontal', label: '↔ Horizontal', desc: 'Independent fields · no cascade' },
                                { id: 'hybrid', label: '⊞ Hybrid', desc: 'Mixed chains + independent fields' },
                              ].map(ht => (
                                <button key={ht.id} onClick={() => saveHierarchyType(activeTabIdx, sIdx, ht.id)}
                                  title={ht.desc}
                                  style={{ fontSize: 9, padding: '2px 9px', borderRadius: 10, border: 'none', cursor: 'pointer', background: currentHType === ht.id ? '#0369a1' : '#e0f2fe', color: currentHType === ht.id ? '#fff' : '#0369a1', fontWeight: 700 }}>
                                  {ht.label}
                                </button>
                              ))}
                              <span style={{ fontSize: 8, color: '#94a3b8' }}>
                                {currentHType === 'vertical' ? '→ Parent→Child chain: selecting a parent value auto-clears & filters child dropdowns' :
                                 currentHType === 'horizontal' ? '→ Each field is independent; no cascade filtering' :
                                 '→ Some fields form chains, others are independent — configure per attribute below'}
                              </span>
                            </div>
                          )}
                          {aeAttrs.length > 0 && (
                            <div style={{ overflowX: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                                <thead>
                                  <tr style={{ background: '#f8faff' }}>
                                    {[
                                      'Attribute', 'Control', 'Visible', 'Required', 'Read-only',
                                      ...(isClassif ? ['Parent Field (Hierarchy)'] : []),
                                      ...(isPricingOrTax ? ['Decimals', 'Formula / Calc'] : []),
                                    ].map((h, i) => (
                                      <th key={i} style={{ padding: '4px 8px', textAlign: i <= 1 ? 'left' : 'center', borderBottom: '1px solid #e2e8f0', fontWeight: 600, color: '#475569', fontSize: 9, whiteSpace: 'nowrap' }}>{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {aeAttrs.map((attr) => {
                                    const isHidden = attr.visible === false;
                                    const isReq = !!attr.IsRequired;
                                    const isRO = !!attr.readonly;
                                    const isCalc = attr.ControlType === 'CalcEdit';
                                    const fmlDisplay = attr.Formula ? attr.Formula.substring(0, 28) + (attr.Formula.length > 28 ? '…' : '') : '';
                                    const attrDp = attr.decimalPlaces || 2;
                                    // For parent dropdown: show other classification attrs (no self)
                                    const parentOpts = isClassif
                                      ? aeAttrs.filter(a => a.AttributeId !== attr.AttributeId && ['grp_classification','grp_style','grp_physical','grp_compliance'].includes(a.GroupId))
                                      : [];
                                    return (
                                      <tr key={attr.AttributeId} style={{ borderBottom: '1px solid #f1f5f9', opacity: isHidden ? 0.45 : 1 }}>
                                        <td style={{ padding: '3px 8px', fontWeight: 600, color: '#1e293b', fontSize: 10 }}>
                                          {attr.Label || attr.AttributeId}
                                          <span style={{ marginLeft: 4, fontSize: 8, color: '#94a3b8' }}>{attr.AttributeId}</span>
                                        </td>
                                        <td style={{ padding: '3px 8px', color: '#64748b', fontSize: 9 }}>{attr.ControlType || 'text'}</td>
                                        <td style={{ padding: '3px 8px', textAlign: 'center' }}>
                                          <button onClick={() => toggleAeAttrDesign(attr.AttributeId, 'visible')}
                                            style={{ fontSize: 9, border: 'none', background: 'transparent', cursor: 'pointer', color: isHidden ? '#dc2626' : '#16a34a', fontWeight: 700 }}>
                                            {isHidden ? '○ Hidden' : '● Visible'}
                                          </button>
                                        </td>
                                        <td style={{ padding: '3px 8px', textAlign: 'center' }}>
                                          <button onClick={() => toggleAeAttrDesign(attr.AttributeId, 'required')}
                                            style={{ fontSize: 9, border: 'none', background: 'transparent', cursor: 'pointer', color: isReq ? '#f59e0b' : '#94a3b8', fontWeight: 700 }}>
                                            {isReq ? '★ Req' : '☆ —'}
                                          </button>
                                        </td>
                                        <td style={{ padding: '3px 8px', textAlign: 'center' }}>
                                          <button onClick={() => toggleAeAttrDesign(attr.AttributeId, 'readonly')}
                                            style={{ fontSize: 9, border: 'none', background: 'transparent', cursor: 'pointer', color: isRO ? '#7c3aed' : '#94a3b8', fontWeight: 700 }}>
                                            {isRO ? 'R/O' : '— Edit'}
                                          </button>
                                        </td>
                                        {isClassif && (
                                          <td style={{ padding: '3px 8px' }}>
                                            <select
                                              value={attr.ParentAttributeId || ''}
                                              onChange={e => saveAttrParent(attr.AttributeId, e.target.value || null)}
                                              style={{ fontSize: 9, padding: '2px 4px', border: '1px solid #e2e8f0', borderRadius: 3, background: '#fff', maxWidth: 140, color: '#334155' }}
                                              data-testid={`parent-select-${attr.AttributeId}`}
                                            >
                                              <option value="">— root (no parent)</option>
                                              {parentOpts.map(p => <option key={p.AttributeId} value={p.AttributeId}>{p.Label || p.AttributeId}</option>)}
                                            </select>
                                          </td>
                                        )}
                                        {isPricingOrTax && (
                                          <td style={{ padding: '3px 8px', textAlign: 'center' }}>
                                            <div style={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                                              {[2,3,4].map(n => (
                                                <button key={n} onClick={() => saveAttrDecimalPlaces(attr.AttributeId, n)}
                                                  title={`${n} decimal places`}
                                                  style={{ fontSize: 8, width: 20, height: 18, border: '1px solid #e2e8f0', borderRadius: 3, cursor: 'pointer', background: attrDp === n ? '#2563eb' : '#f8faff', color: attrDp === n ? '#fff' : '#64748b', fontWeight: attrDp === n ? 700 : 400 }}>
                                                  {n}
                                                </button>
                                              ))}
                                            </div>
                                          </td>
                                        )}
                                        {isPricingOrTax && (
                                          <td style={{ padding: '3px 8px', minWidth: 180 }}>
                                            <button onClick={() => setFormulaBuilderAttr({ ...attr, _sectionSnap: aeSnapshotData })}
                                              data-testid={`formula-btn-${attr.AttributeId}`}
                                              style={{ fontSize: 9, padding: '2px 8px', border: `1px solid ${attr.Formula ? '#86efac' : '#e2e8f0'}`, borderRadius: 4, cursor: 'pointer', background: attr.Formula ? '#f0fdf4' : '#fff', color: attr.Formula ? '#166534' : '#64748b', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', textAlign: 'left' }}
                                              title={attr.Formula || 'Click to open Formula Builder'}>
                                              {attr.Formula ? `= ${fmlDisplay}` : '+ Add Formula'}
                                            </button>
                                          </td>
                                        )}
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      );
                    })()
                  ) : (
                    <>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                          <thead>
                            <tr style={{ background: '#f1f5f9' }}>
                              {['Field', 'Control type', 'Category', 'Visible', 'Read-only', 'Required', 'DB bind', ''].map((h, i) => (
                                <th key={i} style={{ padding: '4px 8px', textAlign: i <= 1 ? 'left' : 'center', borderBottom: '1px solid #e2e8f0', fontWeight: 600, color: '#475569', fontSize: 9, whiteSpace: 'nowrap' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {section.fields.map((rawField, fIdx) => {
                              const field = normalizeField(rawField);
                              const bindKey = `${activeTabIdx}-${sIdx}-${fIdx}`;
                              const hasDb = !!field.db;
                              const cat = TYPE_CAT[field.type] || 'basic';
                              return (
                                <React.Fragment key={`${field.id}-${fIdx}`}>
                                  <tr style={{ borderBottom: '1px solid #f1f5f9' }}
                                    onMouseEnter={e => { if (e.currentTarget) e.currentTarget.style.background = '#fafbff'; }}
                                    onMouseLeave={e => { if (e.currentTarget) e.currentTarget.style.background = ''; }}>
                                    {/* Field label */}
                                    <td style={{ padding: '3px 8px' }}>
                                      <input value={field.label} onChange={e => updateField(activeTabIdx, sIdx, fIdx, { label: e.target.value })}
                                        style={{ fontSize: 10, border: '1px solid transparent', borderRadius: 3, padding: '2px 5px', width: 130, outline: 'none' }}
                                        onFocus={e => e.target.style.border = '1px solid #bfdbfe'}
                                        onBlur={e => e.target.style.border = '1px solid transparent'} />
                                    </td>
                                    {/* Control type */}
                                    <td style={{ padding: '3px 8px' }}>
                                      <select value={field.type} onChange={e => updateField(activeTabIdx, sIdx, fIdx, { type: e.target.value })}
                                        style={{ fontSize: 10, border: '1px solid #e2e8f0', borderRadius: 3, padding: '2px 4px', cursor: 'pointer', maxWidth: 170 }}>
                                        {CONTROL_TYPE_GROUPS.map(g => (
                                          <optgroup key={g.group} label={g.group}>
                                            {g.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                          </optgroup>
                                        ))}
                                      </select>
                                    </td>
                                    {/* Category badge */}
                                    <td style={{ padding: '3px 8px', textAlign: 'center' }}>
                                      <span style={{ fontSize: 8, padding: '1px 5px', borderRadius: 8, background: cat === 'basic' ? '#e0f2fe' : cat === 'advanced' ? '#e0e7ff' : '#fef3c7', color: cat === 'basic' ? '#0369a1' : cat === 'advanced' ? '#4338ca' : '#92400e' }}>{cat}</span>
                                    </td>
                                    {/* Visible toggle */}
                                    <td style={{ padding: '3px 8px', textAlign: 'center' }}>
                                      <ToggleSwitch value={field.visible !== false} onChange={v => updateField(activeTabIdx, sIdx, fIdx, { visible: v })} />
                                    </td>
                                    {/* Read-only toggle */}
                                    <td style={{ padding: '3px 8px', textAlign: 'center' }}>
                                      <ToggleSwitch value={!!field.readonly} onChange={v => updateField(activeTabIdx, sIdx, fIdx, { readonly: v })} />
                                    </td>
                                    {/* Required toggle */}
                                    <td style={{ padding: '3px 8px', textAlign: 'center' }}>
                                      <ToggleSwitch value={!!field.required} onChange={v => updateField(activeTabIdx, sIdx, fIdx, { required: v })} />
                                    </td>
                                    {/* DB bind button */}
                                    <td style={{ padding: '3px 8px', textAlign: 'center' }}>
                                      <button onClick={() => setDbBindOpen(dbBindOpen === bindKey ? null : bindKey)}
                                        title={hasDb ? 'DB bound' : 'Bind to DB'}
                                        style={{ fontSize: 9, padding: '2px 6px', border: `1px solid ${hasDb ? '#6366f1' : '#e2e8f0'}`, borderRadius: 3, cursor: 'pointer', background: hasDb ? '#eef2ff' : '#fff', color: hasDb ? '#4f46e5' : '#94a3b8', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                        <Database size={9} />{hasDb ? 'Bound' : 'DB'}
                                      </button>
                                    </td>
                                    {/* Delete */}
                                    <td style={{ padding: '3px 8px', textAlign: 'center' }}>
                                      <button onClick={() => removeFieldFromSection(activeTabIdx, sIdx, fIdx)}
                                        style={{ padding: '2px 4px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#dc2626', display: 'inline-flex', alignItems: 'center' }}>
                                        <Trash2 size={10} />
                                      </button>
                                    </td>
                                  </tr>
                                  {/* DB bind expanded row */}
                                  {dbBindOpen === bindKey && (
                                    <tr>
                                      <td colSpan={8} style={{ padding: 0 }}>
                                        <DbBindPanel
                                          db={field.db}
                                          fieldType={field.type}
                                          onChange={dbObj => { updateField(activeTabIdx, sIdx, fIdx, { db: dbObj }); if (!dbObj) setDbBindOpen(null); }}
                                        />
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div style={{ padding: '6px 10px', borderTop: '1px solid #f1f5f9' }}>
                        <FieldPicker existingIds={section.fields.map(f => (typeof f === 'string' ? f : f.id))} onAdd={(fObj) => addFieldToSection(activeTabIdx, sIdx, fObj)} />
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Add new section */}
        <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginTop: 8 }}>
          <input value={newSectionName} onChange={e => setNewSectionName(e.target.value)} placeholder="New section name…"
            style={{ fontSize: 10, height: 26, width: 180, border: '1px solid #e2e8f0', borderRadius: 3, padding: '0 6px', outline: 'none' }}
            onKeyDown={e => e.key === 'Enter' && addSection(activeTabIdx)} />
          <button onClick={() => addSection(activeTabIdx)}
            style={{ fontSize: 10, padding: '2px 10px', border: '1px solid #2563eb', color: '#2563eb', borderRadius: 3, cursor: 'pointer', background: '#fff', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Plus size={11} /> Add Section
          </button>
        </div>
      </div>
    );
  };

  // ─── Preview mode — rendered form ──────────────────────────────────────────
  const renderPreviewMode = () => {
    if (!activeTab) return null;
    const onLookupOpen = (f) => {
      if (f.db?.screenId && f.db?.controlId) {
        setPreviewGetInfo({ title: f.label, screenId: f.db.screenId, controlId: String(f.db.controlId) });
      } else if (f.db?.object) {
        setPreviewGetInfo({ title: f.label, tableName: f.db.object });
      } else {
        // Fallback: static SearchInfo map (covers core classification attrs)
        const siCfg = AE_ATTR_SI_MAP[f.id?.toLowerCase?.()];
        if (siCfg) {
          setPreviewGetInfo({ title: f.label, screenId: siCfg.screenId, controlId: String(siCfg.controlId) });
          return;
        }
        // Final fallback: SourceTable from AE snapshot
        const aeAttr = (aeSnapshotData?.attrs || []).find(a => a.AttributeId === f.id);
        const srcTable = aeAttr?.SourceTable;
        if (srcTable) {
          setPreviewGetInfo({ title: f.label, tableName: srcTable });
        } else {
          erpToast.info(`No lookup config found for "${f.label}" — set SourceTable in Attribute Studio`);
        }
      }
    };
    return (
      <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        <div style={{ fontSize: 11, color: '#64748b', marginBottom: 12, padding: '6px 10px', background: '#f1f5f9', borderRadius: 4, borderLeft: '3px solid #2563eb' }}>
          Preview — {industries.find(i => i.IndustryId === selectedIndustryId)?.IndustryName} · Tab: {activeTab.label} · Lookup fields with SearchInfo binding (ScreenId+ControlId) are live
        </div>
        {activeTab.sections.map(section => {
          const visibleFields = section.type === 'ae-classification' ? [] : (section.fields || []).filter(f => normalizeField(f).visible !== false);
          if (section.type === 'ae-classification') {
            // Pricing and Tax groups always have their own dedicated sections — exclude from catch-all only
            const ALWAYS_EXCLUDED = new Set(['grp_pricing', 'grp_tax']);
            const sectionInclude = section.includeGroups?.length ? section.includeGroups : null; // explicit include list
            const snapshotAttrs = aeSnapshotData?.attrs || [];
            const snapshotGroups = (aeSnapshotData?.groups || []).filter(g => {
              // Only exclude pricing/tax from catch-all sections (not from dedicated pricing/tax sections)
              if (!sectionInclude && ALWAYS_EXCLUDED.has(g.GroupId)) return false;
              if (sectionInclude && !sectionInclude.includes(g.GroupId)) return false;
              return true;
            });
            const attrsByGroup = {};
            snapshotAttrs.forEach(a => {
              const gid = a.GroupId || '(none)';
              if (!sectionInclude && ALWAYS_EXCLUDED.has(gid)) return;
              if (sectionInclude && !sectionInclude.includes(gid)) return;
              if (!attrsByGroup[gid]) attrsByGroup[gid] = [];
              attrsByGroup[gid].push(a);
            });
            const nonPricingCount = snapshotAttrs.filter(a => {
              const gid = a.GroupId || '(none)';
              if (!sectionInclude && ALWAYS_EXCLUDED.has(gid)) return false;
              if (sectionInclude && !sectionInclude.includes(gid)) return false;
              return true;
            }).length;
            return (
              <div key={section.id} style={{ marginBottom: 16, border: '1px solid #e0e7ff', borderRadius: 6, overflow: 'hidden' }}>
                <div style={{ padding: '6px 12px', borderLeft: `4px solid ${section.accent || '#3730a3'}`, background: '#f8faff', fontSize: 11, fontWeight: 600, color: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>{section.label}</span>
                  {nonPricingCount > 0 && <span style={{ fontSize: 8, background: '#dbeafe', color: '#1d4ed8', borderRadius: 8, padding: '1px 6px' }}>{nonPricingCount} attributes · ▲▼ to reorder</span>}
                </div>
                {!nonPricingCount ? (
                  <div style={{ padding: '10px 12px', fontSize: 10, color: '#64748b', background: '#f8faff' }}>
                    No AE snapshot available. Click <strong>Refresh AE Snapshot</strong> in the toolbar to load attributes from Industry Config.
                  </div>
                ) : (
                  <div style={{ padding: '10px 12px' }}>
                    {snapshotGroups.map(group => {
                      const gid = group.GroupId;
                      const gAttrs = attrsByGroup[gid] || [];
                      if (!gAttrs.length) return null;
                      return (
                        <div key={gid} style={{ marginBottom: 14 }}>
                          <div style={{ fontSize: 9, fontWeight: 700, color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 7, padding: '2px 8px', background: '#eff6ff', borderRadius: 3, display: 'inline-block' }}>
                            {group.Label}
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px 14px' }}>
                            {gAttrs.map((attr, attrIdxInGroup) => {
                              const fieldType = AE_CONTROL_TYPE_MAP[attr.ControlType] || 'text';
                              const siCfg = AE_ATTR_SI_MAP[attr.AttributeId?.toLowerCase?.()];
                              const f = { id: attr.AttributeId, label: attr.Label || attr.EffectiveLabel || attr.AttributeId, type: fieldType, visible: true, readonly: false, required: !!attr.IsRequired, db: siCfg ? { screenId: siCfg.screenId, controlId: siCfg.controlId } : (attr.SourceTable ? { object: attr.SourceTable } : null) };
                              return (
                                <div key={attr.AttributeId} style={{ position: 'relative', opacity: attr.visible === false ? 0.35 : 1 }}>
                                  <label style={{ display: 'block', fontSize: 9, fontWeight: 600, color: '#475569', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.03em', paddingRight: 14 }}>
                                    {f.label}{f.required && <span style={{ color: '#dc2626', marginLeft: 2 }}>*</span>}
                                  </label>
                                  <PreviewControl field={f} onLookupOpen={onLookupOpen} />
                                  {/* Visibility + Reorder controls — saves to AE snapshot in DB */}
                                  <div style={{ position: 'absolute', top: 0, right: 0, display: 'flex', flexDirection: 'column' }}>
                                    <button onClick={() => toggleAttrVisibility(gid, attrIdxInGroup)}
                                      title={attr.visible === false ? 'Hidden in Item Master — click to show' : 'Visible in Item Master — click to hide'}
                                      data-testid={`attr-visibility-${attr.AttributeId}`}
                                      style={{ fontSize: 8, padding: 0, lineHeight: 1.3, border: 'none', background: 'transparent', cursor: 'pointer', color: attr.visible === false ? '#dc2626' : '#94a3b8', opacity: 0.85 }}>
                                      {attr.visible === false ? '●' : '○'}
                                    </button>
                                    <button onClick={() => moveAttrInGroup(gid, attrIdxInGroup, 'up')} disabled={attrIdxInGroup === 0}
                                      title="Move up" style={{ fontSize: 8, padding: 0, lineHeight: 1.1, border: 'none', background: 'transparent', cursor: attrIdxInGroup === 0 ? 'default' : 'pointer', color: '#94a3b8', opacity: attrIdxInGroup === 0 ? 0.2 : 0.75 }}>▲</button>
                                    <button onClick={() => moveAttrInGroup(gid, attrIdxInGroup, 'down')} disabled={attrIdxInGroup === gAttrs.length - 1}
                                      title="Move down" style={{ fontSize: 8, padding: 0, lineHeight: 1.1, border: 'none', background: 'transparent', cursor: attrIdxInGroup === gAttrs.length - 1 ? 'default' : 'pointer', color: '#94a3b8', opacity: attrIdxInGroup === gAttrs.length - 1 ? 0.2 : 0.75 }}>▼</button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }
          if (!visibleFields.length) return null;
          const cols = section.columns || 3;
          return (
            <div key={section.id} style={{ marginBottom: 16, border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden' }}>
              <div style={{ padding: '6px 12px', borderLeft: `4px solid ${section.accent || '#e2e8f0'}`, background: '#f8faff', fontSize: 11, fontWeight: 600, color: '#1e293b' }}>
                {section.label}
              </div>
              <div style={{ padding: '10px 12px', display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '10px 14px' }}>
                {visibleFields.map((rawField, idx) => {
                  const f = normalizeField(rawField);
                  const hasSearchInfo = !!(f.db?.screenId && f.db?.controlId);
                  return (
                    <div key={`${f.id}-${idx}`}>
                      <label style={{ display: 'block', fontSize: 9, fontWeight: 600, color: '#475569', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                        {f.label}{f.required && <span style={{ color: '#dc2626', marginLeft: 2 }}>*</span>}
                        {hasSearchInfo && <span style={{ marginLeft: 4, fontSize: 8, background: '#dbeafe', color: '#1d4ed8', borderRadius: 6, padding: '0 4px' }}>SI</span>}
                      </label>
                      <PreviewControl field={f} onLookupOpen={onLookupOpen} />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12, paddingTop: 12, borderTop: '1px solid #e2e8f0' }}>
          <button style={{ padding: '4px 14px', fontSize: 10, border: '1px solid #e2e8f0', borderRadius: 3, cursor: 'pointer', background: '#fff', color: '#64748b' }}>Clear</button>
          <button onClick={saveDef} disabled={saving}
            style={{ padding: '4px 14px', fontSize: 10, border: 'none', borderRadius: 3, cursor: 'pointer', background: '#2563eb', color: '#fff', fontWeight: 600 }}>
            {saving ? 'Saving…' : 'Save Configuration'}
          </button>
        </div>
      </div>
    );
  };

  // ─── Main render ──────────────────────────────────────────────────────────
  return (
    <>
    <div style={{ display: 'flex', height: '100%', minHeight: 0, overflow: 'hidden' }} data-testid="item-screens-tab">

      {/* Left: Industry list */}
      <div style={{ width: 200, borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', flexShrink: 0, overflow: 'hidden' }}>
        <div style={{ padding: '8px', borderBottom: '1px solid #e2e8f0', background: '#f8faff' }}>
          <div style={{ fontSize: 9, fontWeight: 700, marginBottom: 4, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Industries</div>
          <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
            <Search size={11} style={{ color: '#94a3b8', flexShrink: 0 }} />
            <input value={industrySearch} onChange={e => setIndustrySearch(e.target.value)} placeholder="Search..."
              style={{ fontSize: 10, height: 24, flex: 1, border: '1px solid #e2e8f0', borderRadius: 3, padding: '0 5px', outline: 'none' }} />
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'auto' }}>
          {filteredIndustries.map(ind => (
            <div key={ind.IndustryId} style={{ display: 'flex', alignItems: 'center', position: 'relative' }}
              onMouseEnter={e => { const btn = e.currentTarget && e.currentTarget.querySelector('.del-btn'); if (btn) btn.style.opacity = '1'; }}
              onMouseLeave={e => { const btn = e.currentTarget && e.currentTarget.querySelector('.del-btn'); if (btn) btn.style.opacity = '0'; }}>
              <button onClick={() => selectIndustry(ind.IndustryId)} data-testid={`industry-row-${ind.IndustryId}`}
                style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', textAlign: 'left', padding: '5px 10px', fontSize: 11, cursor: 'pointer', border: 'none', background: selectedIndustryId === ind.IndustryId ? '#eff6ff' : 'transparent', borderLeft: `3px solid ${selectedIndustryId === ind.IndustryId ? '#2563eb' : 'transparent'}`, fontWeight: selectedIndustryId === ind.IndustryId ? 700 : 400, color: '#1e293b' }}>
                <span>{ind.Icon && <span style={{ marginRight: 4 }}>{ind.Icon}</span>}{ind.IndustryName}</span>
                {definedIndustries.includes(ind.IndustryId) && (
                  <span style={{ fontSize: 8, background: '#dcfce7', color: '#16a34a', borderRadius: 8, padding: '1px 5px', fontWeight: 700 }}>✓</span>
                )}
              </button>
              {definedIndustries.includes(ind.IndustryId) && (
                <button className="del-btn"
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (deleteConfirm !== ind.IndustryId) { setDeleteConfirm(ind.IndustryId); setTimeout(() => setDeleteConfirm(null), 3000); return; }
                    const r = await fetch(`${API_URL}/api/attribute-engine/screen-definitions/${ind.IndustryId}`, { method: 'DELETE', headers: hdrs() });
                    const j = await r.json();
                    if (j.success) { setDefinedIndustries(p => p.filter(x => x !== ind.IndustryId)); if (selectedIndustryId === ind.IndustryId) setSelectedIndustryId(''); }
                    setDeleteConfirm(null);
                  }}
                  title={deleteConfirm === ind.IndustryId ? 'Click again to confirm' : 'Remove definition'}
                  style={{ opacity: 0, transition: 'opacity 0.15s', padding: '2px 6px', marginRight: 4, fontSize: 9, background: deleteConfirm === ind.IndustryId ? '#fef2f2' : 'transparent', border: `1px solid ${deleteConfirm === ind.IndustryId ? '#ef4444' : '#fca5a5'}`, borderRadius: 4, color: '#dc2626', cursor: 'pointer' }}
                  data-testid={`del-def-${ind.IndustryId}`}>
                  {deleteConfirm === ind.IndustryId ? '✓ Sure?' : '✕'}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Right: Builder */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        {!selectedIndustryId ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>
            Select an industry from the left to view and edit its Item Master screen definition.
          </div>
        ) : loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>Loading screen definition…</div>
        ) : (
          <>
            {renderToolbar()}
            {snapshotStale && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', background: '#fefce8', borderBottom: '1px solid #fde68a', fontSize: 11, color: '#92400e', flexShrink: 0 }}
                data-testid="snapshot-stale-banner">
                <AlertTriangle size={13} style={{ flexShrink: 0 }} />
                <span>Pricing/Tax library attributes have changed since the last snapshot was baked.</span>
                <button onClick={refreshSnapshot}
                  style={{ marginLeft: 4, fontSize: 10, padding: '2px 8px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 3, fontWeight: 600, cursor: 'pointer' }}>
                  Refresh Snapshot Now
                </button>
                <button onClick={() => setSnapshotStale(false)}
                  style={{ fontSize: 11, padding: '0 4px', background: 'transparent', color: '#92400e', border: 'none', cursor: 'pointer', marginLeft: 'auto' }}>
                  ✕
                </button>
              </div>
            )}
            {renderTabBar()}
            {mode === 'design' ? renderDesignMode() : renderPreviewMode()}
            <div style={{ padding: '5px 12px', borderTop: '1px solid #e2e8f0', fontSize: 9, color: '#94a3b8', background: '#f8faff', flexShrink: 0 }}>
              Saves to WebApp.IM_ScreenDefinitions · Read by ItemMasterDynamic per industry
            </div>
          </>
        )}
      </div>
    </div>
    {/* Formula Builder Modal */}
    {formulaBuilderAttr && (
      <FormulaBuilderModal
        attr={formulaBuilderAttr}
        snapshot={aeSnapshotData}
        onSave={(fml, dp) => {
          saveAttrFormula(formulaBuilderAttr.AttributeId, fml, dp);
          setFormulaBuilderAttr(null);
        }}
        onClose={() => setFormulaBuilderAttr(null)}
      />
    )}
    {/* Live GetInfo popup for Preview mode */}
    {previewGetInfo && (
      <GetInfoPopup
        title={previewGetInfo.title}
        tableName={previewGetInfo.tableName}
        screenId={previewGetInfo.screenId}
        controlId={previewGetInfo.controlId}
        onSelect={() => setPreviewGetInfo(null)}
        onClose={() => setPreviewGetInfo(null)}
      />
    )}
    </>
  );
};

export default ItemScreensTab;
